import moment from "moment";
import { toast } from "react-toastify";
import { push } from "connected-react-router";
import { modelNameMap } from "./modelNames";

import * as ScoreAPI from "api/ScoreAPI";

import {
  getConversionGain,
  getConversionCost,
  getConversionMissed,
  getConversionSaved,
  getCostSliderRatio,
  getOutcomeType,
  getTotalPossibleHits,
  getTotal,
  getTruePositive,
  getTrueNegative,
  getFalsePositive,
  getFalseNegative,
  getProcessLimit,
  getCurrentROI,
  getHideCosts,
  getMode as getSetupMode,
  getIsQuickRun,
  getIsTwoMinuteRun,
  getNetImpactLabel,
  getLabels,
  getSetup,
  getSplitColumn,
  initSetup,
  setDefaults,
  getDatasetFromSetup,
  updateSetup,
  updateSetups
} from "containers/Setup/reducer";

import { getRunOffset, updateIsJobsLoading } from "containers/App/reducer";
import { fetchDataset, getDataset, getFields } from "containers/Home/reducer";
import { updateJobId, updateStep } from "containers/CreateWizard/reducer";
import { updateRightKey } from "containers/Job/reducer";
import * as accountReducer from "containers/Accounts/reducer";
import * as wizardReducer from "containers/Wizard/reducer";
import * as loginReducer from "containers/Login/reducer";
import * as setupReducer from "containers/Setup/reducer";
import * as paginationReducer from "containers/Pagination/reducer";
import * as predictReducer from "containers/Predict/reducer";
import * as projectReducer from "containers/Project/reducer";
import * as deployReducer from "containers/Deployments/reducer";
import * as globalReducer from "containers/App/reducer";

import { STEP_TYPE as WIZARD_STEP_TYPE } from "containers/Wizard/reducer";

import * as ModelAPI from "api/ModelAPI";

import { filterOtherUsers, getIsMarketplace } from "containers/Marketplace/reducer";

import { isEnabled } from "utils/featureFlags";

import { ConfusionMatrixFactoryFactory, ModelFactory, ModelFactoryConfig } from "utils/stats";

import { MINI_BLUEPRINTS } from "components/Results/AcademicResults";

export const LOAD_MODELS = "run/LOAD_MODELS";
export const UPDATE_FIELD = "run/UPDATE_FIELD";
export const UPDATE_JOB = "run/UPDATE_JOB";
export const UPDATE_JOBS = "run/UPDATE_JOBS";
export const REPLACE_JOBS = "run/REPLACE_JOBS";
export const UPDATE_MODEL = "run/UPDATE_MODEL";
export const UPDATE_MODELS = "run/UPDATE_MODELS";
export const UPDATE_FORECAST = "run/UPDATE_FORECAST";
export const RESET = "run/RESET";

export const GRAPH_TYPES = {
  COST_COMPARISON: "Cost Comparison",
  CAPACITY_COMPARISON: "Capacity Comparison",
  ACCURACY: "Impact vs Accuracy",
  PROCESSED: "Impact vs Processed",
  LOG_LOSS: "Impact vs Log Loss",
  COUNTS: "Raw Counts",
  COSTS: "Cost Ratio",
  ERROR_VS_AVERAGE: "Total Error Cost vs Average Prediction Error"
};

export const PROJECT_MODES = {
  RUN: "run",
  MONITOR: "monitor",
  MONITOR_DEMO: "monitor_demo",
  MONITOR_GROUP: "monitor_group",
  AUDIT: "audit"
};

export const PANEL_MODES = {
  SELECTED_MODEL: "select_model",
  MODEL_GROUPS: "model_groups",
  CLOSED: null
};

export const CAPACITY_BREAKPOINTS = {};

for (let i = 0; i <= 100; i += 1) {
  CAPACITY_BREAKPOINTS[`${i}%`] = i / 100;
}

export const CAPACITY_BREAKPOINTS_ALL = {};

for (let i = 0; i <= 100; i += 1) {
  CAPACITY_BREAKPOINTS_ALL[`${i}%`] = i / 100;
}

export const FORECAST_DEFAULT_GRAPH_DISPLAY = 60;

const emptyJobState = {
  models: {},
  serverProgress: 0,
  notebookProgress: 0,
  iterationProgress: 0,
  completedProgress: 0,
  featureSelection: undefined,
  predictionDriversMode: "drillDown",
  status: undefined,
  deploy: undefined,
  showDeploy: false,
  isScoreUploading: false,
  isPredictLoading: false,
  isRightOpen: false,
  panelMode: PANEL_MODES.CLOSED,
  isNextStep: false,
  predictMode: "drivers",
  runPlotType: null,
  runPlotCapacity: [0, 100],
  capacityRange: null,
  isBusinessReality: true,
  mode: PROJECT_MODES.RUN,
  ignoredModels: {},
  wizardOptions: {
    [WIZARD_STEP_TYPE.CHOOSE_DATASET.id]: {
      selectedTab: WIZARD_STEP_TYPE.CHOOSE_DATASET.tabs.CHOOSE_DATASET
    },
    [WIZARD_STEP_TYPE.ACADEMIC_EVALUATION.id]: {
      selectedTab: WIZARD_STEP_TYPE.ACADEMIC_EVALUATION.tabs.PREDICTION_DRIVERS
    },
    [WIZARD_STEP_TYPE.SUCCESS.id]: {
      selectedTab: WIZARD_STEP_TYPE.SUCCESS.tabs.OVERVIEW
    }
  }
};

const initialState = {};

function runReducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_MODEL:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          ["models"]: {
            ...state[action.id].models,
            [action.modelId]: action.data
          }
        }
      };
    case UPDATE_FORECAST:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          ["forecastResults"]: {
            ...state[action.id].forecastResults,
            [action.modelId]: action.data
          }
        }
      };
    case UPDATE_FIELD:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          [action.key]: action.data
        }
      };
    case UPDATE_JOBS:
      return {
        ...state,
        ...action.data
      };
    case REPLACE_JOBS:
      return {
        ...action.data
      };
    case UPDATE_JOB:
      return {
        ...state,
        [action.id]: action.data
      };
    case RESET:
      return {
        ...initialState
      };
    default:
      return state;
  }
}

/* Selectors */
export function getIsModelsLegacy() {
  return isEnabled("models-legacy");
}

export function getJobs(state) {
  return state.run;
}

export function getJob(state, id) {
  return getJobs(state)[id] !== undefined ? getJobs(state)[id] : {};
}

export function getJobSetup(state, id) {
  return (getJob(state, id) || {}).jobSetup;
}

export function getAllForecastResults(state, id) {
  return getJob(state, id).forecastResults;
}

export function getForecastResults(state, id, modelId) {
  const retModelId = modelId ? modelId : getForecastModelId(state, id);
  return (getAllForecastResults(state, id) || {})[retModelId];
}

export function getIsForecastLoading(state, id) {
  return getJob(state, id).isForecastLoading;
}

export function updateIsForecastLoading(id, val = false) {
  return {
    type: UPDATE_FIELD,
    id: id,
    key: "isForecastLoading",
    data: val
  };
}

export function getForecastChartDateRange(state, id) {
  const run = getJob(state, id);
  if (run && run.forecastChartDateRange) {
    return run.forecastChartDateRange;
  }
  const forecastResults = getForecastResults(state, id);

  const originalSeriesLength =
    forecastResults && forecastResults.prediction_plot && forecastResults.prediction_plot.original_series_timestamps
      ? forecastResults.prediction_plot.original_series_timestamps.length
      : 0;

  const predictionLength =
    forecastResults && forecastResults.prediction_plot && forecastResults.prediction_plot.prediction_timestamps
      ? forecastResults.prediction_plot.prediction_timestamps.length
      : 0;

  const maxIndex = Math.max(0, originalSeriesLength + predictionLength - 1);
  const minIndex = Math.max(0, maxIndex - 5 * predictionLength);

  return [minIndex, maxIndex];
}

export function updateForecastChartDateRange(id, dateRange) {
  return {
    type: UPDATE_FIELD,
    id: id,
    key: "forecastChartDateRange",
    data: dateRange
  };
}

export function fetchForecast(id, modelIdOverride = undefined, skipIfExists = false) {
  return (dispatch, getState) => {
    const projectId = projectReducer.getProjectIdFromRunId(getState(), id);
    const accountId = projectReducer.getProjectAccountId(getState(), projectId);

    // TODO: add model selection and model details to Forecast
    const selectedModel = modelIdOverride !== undefined ? modelIdOverride : getForecastModelId(getState(), id);

    const forecastResults = getForecastResults(getState(), id, selectedModel);

    if (!selectedModel || (skipIfExists && forecastResults)) {
      return Promise.resolve();
    }

    const model = getModel(getState(), id, selectedModel) ?? {};

    if (model.extended !== undefined) {
      dispatch({
        type: UPDATE_FORECAST,
        id: id,
        modelId: selectedModel,
        data: model.extended
      });

      return Promise.resolve();
    }

    const time = setupReducer.getForecastTime(getState(), id);
    const unit = setupReducer.getForecastTimeUnit(getState(), id);

    dispatch(updateIsForecastLoading(id, true));

    return ScoreAPI.getBulkScoreEndpoint(accountId).then((eRes) => {
      const endpointResponse =
        eRes && eRes.configuration && eRes.configuration.executor ? eRes.configuration.executor : {};
      if (!endpointResponse.endpoint_url) {
        toast(`Endpoint not found. Please ensure there is an active executor for this account.`, {
          type: toast.TYPE.ERROR,
          position: toast.POSITION.TOP_CENTER,
          autoClose: 7500
        });
        dispatch(updateIsForecastLoading(id, false));
        return Promise.resolve();
      }
      const rawURL = String(endpointResponse.endpoint_url);
      const headers = endpointResponse.endpoint_headers;
      let retURL = rawURL.substring(0, rawURL.length - 6) + `job/${id}/${selectedModel}/info`;
      return ScoreAPI.fetchForecast(projectId, retURL, headers, time, unit)
        .then((res) => {
          dispatch({
            type: UPDATE_FORECAST,
            id: id,
            modelId: selectedModel,
            data: res
          });
          dispatch(updateIsForecastLoading(id, false));
        })
        .catch((err) => {
          console.log(err);
          toast("Error fetching Forecasting results", {
            type: toast.TYPE.ERROR,
            position: toast.POSITION.TOP_CENTER,
            autoClose: 7500
          });
          dispatch(updateIsForecastLoading(id, false));
        });
    });
  };
}

// Helper for forecast plots
// Currently limiting display to 5x the prediction length.
function getOriginalSeriesDisplayIndex(originalSeriesArray, predictionArray) {
  return 0;
  const originalSeriesDisplayLimit = Math.min(predictionArray.length * 5, originalSeriesArray.length);
  const originalSeriesDisplayIndex = originalSeriesArray.length - originalSeriesDisplayLimit;
  return originalSeriesDisplayIndex;
}

// Get the moment format string for an array of time stamps
function getTimeSeriesFormatString(timestamps) {
  if (!timestamps || timestamps.length < 2) {
    return "MMM D YYYY H:mm:ss";
  }

  // Find the smallest unit of time shared between data points
  const timeUnits = ["year", "day", "hour", "second"];
  let timeDiffIndex = null;

  // Default is to only show years (each datapoint is a different year)
  let formatString = "YYYY";

  for (let i = 0; i < timestamps.length - 1; i++) {
    const currTime = moment(timestamps[i]);
    const nextTime = moment(timestamps[i + 1]);

    for (let tIndex = timeDiffIndex === null ? 0 : timeDiffIndex; tIndex < timeUnits.length; tIndex++) {
      const tUnit = timeUnits[tIndex];
      if (currTime.isSame(nextTime, tUnit)) {
        timeDiffIndex = tIndex;
      }
    }
  }

  // Construct the format string based upon the smallest unit of time shared
  if (timeDiffIndex !== null) {
    if (timeDiffIndex >= 0) {
      formatString = "MMM D " + formatString;
    }
    if (timeDiffIndex >= 1) {
      formatString += " H:mm";
    }
    if (timeDiffIndex >= 2) {
      formatString += ":ss";
    }
    if (timeDiffIndex >= 3) {
      formatString += ":SSS";
    }
  }

  return formatString;
}

// Returns the array of all the timestamps for the original and prediction
export function getForecastPredictionTimestamps(state, id, modelId) {
  const forecastResults = getForecastResults(state, id, modelId);
  const predictionPlot = forecastResults && forecastResults.prediction_plot;

  let data = [];

  if (predictionPlot && Object.keys(predictionPlot).length) {
    const originalSeriesTime = predictionPlot.original_series_timestamps || [];
    const predictiontime = predictionPlot.prediction_timestamps || [];
    data = [...originalSeriesTime, ...predictiontime];
  }
  return data;
}

export function getForecastQuantile(state, id, modelId) {
  const forecastResults = getForecastResults(state, id, modelId);
  const predictionPlot = forecastResults && forecastResults.prediction_plot;

  if (!predictionPlot) {
    return {};
  }

  // Quantile limits (inclusive)
  const QUANTILE_LOWER_LIMIT = 0.1;
  const QUANTILE_UPPER_LIMIT = 0.9;

  let quantileLowerIndex = undefined;
  let quantileUpperIndex = undefined;

  const quantilesArr = predictionPlot.target_quantiles || [];

  // Find the indices for the quantile limits
  if (quantilesArr) {
    for (let i = 0; i < quantilesArr.length; i++) {
      const currQuantile = quantilesArr[i];
      if (quantileLowerIndex === undefined && currQuantile > QUANTILE_LOWER_LIMIT) {
        quantileLowerIndex = Math.max(0, i - 1);
      }
      if (quantileUpperIndex === undefined && currQuantile >= QUANTILE_UPPER_LIMIT) {
        quantileUpperIndex = Math.min(quantilesArr.length - 1, i);
      }
    }
  }

  // Set the indices to the first/last indices if the limits aren't found
  if (quantileLowerIndex === undefined) {
    quantileLowerIndex = 0;
  }
  if (quantileUpperIndex === undefined) {
    quantileUpperIndex = quantilesArr ? quantilesArr.length - 1 : 0;
  }

  let quantileIndex = undefined;
  if (Object.keys(predictionPlot).length) {
    // Use the local ratio instead of the default one if the user has moved the slider
    const useForecastLocalRatio = setupReducer.getUseForecastLocalRatio(state, id);
    const forecastLocalRatio = setupReducer.getForecastLocalRatio(state, id);
    if (useForecastLocalRatio) {
      // Limit the slider ratio by the quantile limits
      const targetQuantile = Math.max(
        QUANTILE_LOWER_LIMIT,
        Math.min(QUANTILE_UPPER_LIMIT, 1 / (forecastLocalRatio + 1))
      );
      for (let qIndex = quantileLowerIndex; qIndex <= quantileUpperIndex; qIndex++) {
        const currQuantile = quantilesArr[qIndex];
        // Once we pass the quantile target, pick the one closer to the target between the current and previous
        if (quantileIndex === undefined && targetQuantile <= currQuantile) {
          const prevIndex = Math.max(qIndex - 1, quantileLowerIndex);
          const prevQuantile = quantilesArr[prevIndex];
          if (Math.abs(currQuantile - targetQuantile) <= Math.abs(targetQuantile - prevQuantile)) {
            quantileIndex = qIndex;
          } else {
            quantileIndex = prevIndex;
          }
        }
      }
    }
  }

  return {
    quantileIndex,
    quantile: quantileIndex !== undefined ? quantilesArr[quantileIndex] : 0.5,

    quantileLowerLimitIndex: quantileLowerIndex,
    quantileLowerLimit: quantilesArr[quantileLowerIndex],

    quantileUpperLimitIndex: quantileUpperIndex,
    quantileUpperLimit: quantilesArr[quantileUpperIndex]
  };
}

export function getForecastPredictionPlot(state, id, modelId) {
  const forecastResults = getForecastResults(state, id, modelId);
  const predictionPlot = forecastResults && forecastResults.prediction_plot;

  let data = [];

  if (!predictionPlot) {
    return data;
  }

  if (Object.keys(predictionPlot).length) {
    // There is an unused array between the prediction of the components (trend, seasonal, etc) and the actual prediction
    const MEAN_PREDICTION_INDEX = predictionPlot.component_names ? predictionPlot.component_names.length + 1 : 4;
    const meanPredictionArray = predictionPlot.mean_prediction
      ? predictionPlot.mean_prediction[Math.min(MEAN_PREDICTION_INDEX, predictionPlot.mean_prediction.length - 1)]
      : [];
    const totalLength = predictionPlot.original_series.length + meanPredictionArray.length;

    // Calculates the moment time format to be used base on the differences between the time stamps
    const timeSeries = getForecastPredictionTimestamps(state, id, modelId);
    const timeFormat = getTimeSeriesFormatString(timeSeries);

    const originalSeriesDisplayIndex = getOriginalSeriesDisplayIndex(
      predictionPlot.original_series,
      meanPredictionArray
    );

    // Use the local ratio instead of the default one if the user has moved the slider
    const useForecastLocalRatio = setupReducer.getUseForecastLocalRatio(state, id);
    const { quantileIndex, quantileLowerLimitIndex, quantileUpperLimitIndex } = getForecastQuantile(state, id, modelId);

    // Construct the plot data using the original series length as the pivot to switch into predictions
    for (let i = originalSeriesDisplayIndex; i < totalLength; i++) {
      if (i < predictionPlot.original_series.length) {
        const actualVal = predictionPlot.original_series[i];
        data.push({
          Date: moment(timeSeries[i]).format(timeFormat),
          Actual: actualVal,
          Range: [actualVal, actualVal],
          Prediction: i !== predictionPlot.original_series.length - 1 ? undefined : actualVal,
          hidePrediction: true
        });
      } else {
        const currentIndex = i - predictionPlot.original_series.length;
        let predictionVal = meanPredictionArray[currentIndex];
        if (useForecastLocalRatio && quantileIndex !== undefined) {
          predictionVal = predictionPlot.prediction_quantiles[quantileIndex][currentIndex];
        }
        const upperBound = predictionPlot.prediction_quantiles[quantileUpperLimitIndex][currentIndex];
        const lowerBound = predictionPlot.prediction_quantiles[quantileLowerLimitIndex][currentIndex];
        data.push({
          Date: moment(timeSeries[i]).format(timeFormat),
          Actual: undefined,
          Prediction: predictionVal,
          Range: [lowerBound, upperBound]
        });
      }
    }
  }
  return data;
}

export function getForecastTrendPlot(state, id, modelId) {
  const forecastResults = getForecastResults(state, id, modelId);
  const predictionPlot = forecastResults && forecastResults.prediction_plot;
  const predictionComponentsPlot =
    forecastResults && forecastResults.prediction_components_plot ? forecastResults.prediction_components_plot : {};
  const componentPlots = forecastResults && forecastResults.component_plot;

  let data = [];

  if (predictionPlot && Object.keys(predictionPlot).length) {
    const componentNames = predictionPlot.component_names || [];

    // Finds the references for the seasonal, trend and other values
    // Seasonal and Trend will use the first instance found in component_names
    // Other will be an array of all the values in component_names not in trend or seasonal
    let TREND_COMPONENT_KEY;
    let SEASONAL_COMPONENT_KEY;
    let OTHER_COMPONENT_KEYS = [];
    componentNames.forEach((cName, index) => {
      if (TREND_COMPONENT_KEY === undefined && cName.includes("Trend")) {
        TREND_COMPONENT_KEY = cName;
      } else if (SEASONAL_COMPONENT_KEY === undefined && (cName.includes("Seasonal") || cName.includes("Trig"))) {
        SEASONAL_COMPONENT_KEY = cName;
      } else {
        OTHER_COMPONENT_KEYS.push(cName);
      }
    });
    if (componentPlots["residuals"] && !OTHER_COMPONENT_KEYS.includes("residuals")) {
      OTHER_COMPONENT_KEYS.push("residuals");
    }

    const trendOriginalSeries = TREND_COMPONENT_KEY ? componentPlots[TREND_COMPONENT_KEY] : [];
    const trendArray =
      predictionComponentsPlot && TREND_COMPONENT_KEY !== undefined && predictionComponentsPlot[TREND_COMPONENT_KEY]
        ? predictionComponentsPlot[TREND_COMPONENT_KEY]
        : [];

    const seasonalOriginalSeries = SEASONAL_COMPONENT_KEY ? componentPlots[SEASONAL_COMPONENT_KEY] : [];
    const seasonalArray =
      predictionComponentsPlot &&
      SEASONAL_COMPONENT_KEY !== undefined &&
      predictionComponentsPlot[SEASONAL_COMPONENT_KEY]
        ? predictionComponentsPlot[SEASONAL_COMPONENT_KEY]
        : [];

    // Calculates the cumulative values for the `Other Factors` original series
    let oLength = 0;
    OTHER_COMPONENT_KEYS.forEach((cKey) => {
      const cSeries = componentPlots[cKey] || [];
      oLength = Math.max(cSeries.length, oLength);
    });
    const otherOriginalSeries = new Array(oLength);
    for (let i = 0; i < oLength; i++) {
      let currValue = 0;
      OTHER_COMPONENT_KEYS.forEach((cKey) => {
        const cSeries = componentPlots[cKey];
        if (cSeries && !isNaN(cSeries[i])) {
          currValue += cSeries[i];
        }
      });
      otherOriginalSeries[i] = currValue;
    }

    // Calculates the cumulative values for the `Other Factors` prediction
    let opLength = 0;
    OTHER_COMPONENT_KEYS.forEach((cKey) => {
      const pSeries = predictionComponentsPlot[cKey] || [];
      opLength = Math.max(pSeries.length, opLength);
    });
    const otherArray = new Array(opLength);
    for (let i = 0; i < opLength; i++) {
      let currValue = 0;
      OTHER_COMPONENT_KEYS.forEach((cKey) => {
        const cSeries = predictionComponentsPlot[cKey];
        if (cSeries && !isNaN(cSeries[i])) {
          currValue += cSeries[i];
        }
      });
      otherArray[i] = currValue;
    }

    // Find the full length of the original data
    const originalLength = Math.max(trendOriginalSeries.length, seasonalOriginalSeries.length);
    const componentLength = Math.max(trendArray.length, seasonalArray.length);

    const totalLength = originalLength + componentLength;

    // Only shows 5x the prediction time
    const originalTrendDisplayIndex = getOriginalSeriesDisplayIndex(trendOriginalSeries, trendArray);
    const originalSeasonalDisplayIndex = getOriginalSeriesDisplayIndex(seasonalOriginalSeries, seasonalArray);

    const originalSeriesDisplayIndex = Math.max(originalTrendDisplayIndex, originalSeasonalDisplayIndex);

    const timeSeries = getForecastPredictionTimestamps(state, id, modelId);
    const timeFormat = getTimeSeriesFormatString(timeSeries);

    // Construct the actual plot data using the originalLength to indicate when to pivot to the prediction
    for (let i = originalSeriesDisplayIndex; i < totalLength; i++) {
      if (i < originalLength) {
        const trendValue = trendOriginalSeries[i];
        const seasonalValue = seasonalOriginalSeries[i];
        const otherValue = otherOriginalSeries[i];
        data.push({
          Date: moment(timeSeries[i]).format(timeFormat),
          Trend: trendValue,
          Seasonal: seasonalValue,
          Other: otherValue,
          TrendPrediction: i !== originalLength - 1 ? undefined : trendValue,
          SeasonalPrediction: i !== originalLength - 1 ? undefined : seasonalValue,
          OtherPrediction: i !== originalLength - 1 ? undefined : otherValue,
          hidePrediction: true
        });
      } else {
        const currentIndex = i - originalLength;
        let trendPrediction = trendArray[currentIndex];
        let seasonalPrediction = seasonalArray[currentIndex];
        let otherPrediction = otherArray[currentIndex];

        data.push({
          Date: moment(timeSeries[i]).format(timeFormat),
          Trend: undefined,
          Seasonal: undefined,
          Other: undefined,
          TrendPrediction: trendPrediction,
          SeasonalPrediction: seasonalPrediction,
          OtherPrediction: otherPrediction
        });
      }
    }
  }
  return data;
}

export function getForecastModels(state, id) {
  const models = getModelsArr(state, id) || [];

  models.sort((a, b) => {
    const aVal = a && a.metrics ? a.metrics.mean_absolute_error : Infinity;
    const bVal = b && b.metrics ? b.metrics.mean_absolute_error : Infinity;
    return aVal - bVal;
  });
  return models;
}

export function getForecastModelOptions(state, id) {
  const MODEL_TYPE_TRANSLATIONS = {
    Bsts: "Bayesian Structural Time Series"
  };

  const MODEL_TREND_TRANSLATIONS = {
    local_level: "Local Level",
    local_linear_trend: "Local Linear Trend",
    semilocal_linear_trend: "Semilocal Linear Trend",
    student_local_linear_trend: "Student Local Linear Trend"
  };

  const ERROR_DISTRIBUTION_TRANSLATIONS = {
    student: "Student",
    gaussian: "Gaussian"
  };

  const MODEL_SEASONAL_TRANSLATIONS = {
    seasonal: "Seasonal",
    trig: "Trig"
  };

  const OTHER_FACTORS_TRANSLATIONS = {
    autoar: "AAR"
  };

  const models = getForecastModels(state, id);
  return models.map((m, i) => {
    const modelNumber = i + 1;
    let labelModifier = i === 0 ? " (Optimal)" : "";

    const modelType = m.structure && m.structure.type ? MODEL_TYPE_TRANSLATIONS[m.structure.type] : "None";
    const trendModel =
      m.structure && m.structure.components && m.structure.components.trend
        ? MODEL_TREND_TRANSLATIONS[m.structure.components.trend]
        : "None";
    const errorDistribution =
      m.structure && m.structure.family ? ERROR_DISTRIBUTION_TRANSLATIONS[m.structure.family] : "None";

    let seasonalModel = "None";
    const otherFactorsArr = [];
    m.structure &&
      m.structure.components &&
      Object.keys(m.structure.components).forEach((cName, i) => {
        if (cName !== "trend") {
          if (cName.includes("seasonal") && seasonalModel === "None") {
            seasonalModel = MODEL_SEASONAL_TRANSLATIONS["seasonal"];
          } else if (cName.includes("trig") && seasonalModel === "None") {
            seasonalModel = MODEL_SEASONAL_TRANSLATIONS["trig"];
          } else if (OTHER_FACTORS_TRANSLATIONS[cName] !== undefined) {
            otherFactorsArr.push(OTHER_FACTORS_TRANSLATIONS[cName]);
          }
        }
      });

    let otherFactors = otherFactorsArr.length ? otherFactorsArr : "None";

    return {
      label: m.label + labelModifier,
      model_number: modelNumber,
      value: m.id,
      id: m.id,
      name: m.label,
      mean_absolute_error: m.metadata && m.metadata.mean_absolute_error,
      MEAU: m.metadata && m.metadata.MEAU,
      r2_score: m.metadata && m.metadata.r2_score,
      model_type: modelType,
      trend_model: trendModel,
      error_distribution: errorDistribution,
      model_seasonal: seasonalModel,
      other_factors: otherFactors
    };
  });
}

// seasonal/trig - annual weekday hourly
// trend
// rest is other factors

export function updateAndFetchForecastModel(id, modelId) {
  return (dispatch, getState) => {
    const forecastResults = getForecastResults(getState(), id, modelId);
    dispatch(setupReducer.updateForecastModelSelection(id, modelId));
    if (!forecastResults) {
      return dispatch(fetchForecast(id, modelId));
    } else {
      return Promise.resolve();
    }
  };
}

// Currently selects the model with the lowest mean absolute error
// Eventually we will give the user the ability to select
export function getForecastModel(state, id, modelIdOverride = undefined) {
  const models = getForecastModels(state, id);

  const retModelId =
    modelIdOverride !== undefined ? modelIdOverride : setupReducer.getForecastModelSelection(state, id);

  const retModel = (retModelId !== undefined && models.find((m) => m && m.id === retModelId)) || models[0];

  return retModel;
}

export function getForecastModelId(state, id) {
  const fModel = getForecastModel(state, id);

  return fModel && fModel.id;
}

export function downloadForecast(id, modelIdOverride = undefined) {
  return (dispatch, getState) => {
    const predictionPlot = (getForecastPredictionPlot(getState(), id, modelIdOverride) || []).filter(
      (e) => !e.hidePrediction
    );

    //praise stack overflow
    const objectsToCSV = (arr) => {
      const array = [Object.keys(arr[0])].concat(arr);
      return array
        .map((row) => {
          return Object.values(row)
            .map((value) => {
              return typeof value === "string" ? JSON.stringify(value) : value;
            })
            .toString();
        })
        .join("\n");
    };

    const transformedPredictionPlot = predictionPlot.map((record) => ({
      Date: record.Date,
      Prediction: record.Prediction,
      "Lower Bound": record.Range && record.Range.length > 0 ? record.Range[0] : record.Prediction,
      "Upper Bound": record.Range && record.Range.length > 0 ? record.Range[record.Range.length - 1] : record.Prediction
    }));

    const predictionPlotCsvString = objectsToCSV(transformedPredictionPlot);
    const filename = "forecast_export.csv";
    const blob = new Blob([predictionPlotCsvString], {
      type: "text/csv;charset=utf-8;"
    });
    if (navigator.msSaveBlob) {
      // IE 10+
      navigator.msSaveBlob(blob, filename);
    } else {
      const link = document.createElement("a");
      if (link.download !== undefined) {
        // feature detection
        // Browsers that support HTML5 download attribute
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };
}

export function getIgnored(state, id) {
  return getJob(state, id).ignored || {};
}

export function getIgnoredValue(state, id) {
  return getJob(state, id).ignoredValue || {};
}

export function updateCurrentIgnored(id, ignored) {
  return {
    type: UPDATE_FIELD,
    id: id,
    key: "ignored",
    data: ignored
  };
}

export function updateCurrentIgnoredValue(id, ignoredValue) {
  return {
    type: UPDATE_FIELD,
    id: id,
    key: "ignoredValue",
    data: ignoredValue
  };
}

export function getJobOutcome(state, id) {
  return getJob(state, id).outcome;
}

export function getOutcome(state, id) {
  return getJob(state, id).outcome;
}

export function getHasModels(state, id) {
  const models = getModelsArr(state, id);

  return models && models.length > 0 ? true : false;
}

export function getJobsCount(state) {
  return state.run === undefined ? 0 : Object.keys(state.run).length;
}

export function getJobsByArr(state) {
  return Object.keys(state.run).map((key) => ({
    id: key,
    ...state.run[key]
  }));
}

export function getSortedJobsByArr(state) {
  return getJobsByArr(state)
    .sort((a, b) => b.id - a.id)
    .map((run) => ({
      ...run,
      dataset: getDataset(state, run.datasetId)
    }));
}

export function getJobAccountId(state, id) {
  return getJob(state, id) ? getJob(state, id).accountId : undefined;
}

export function getTransformedJob(state, id) {
  const job = getJob(state, id);

  return {
    ...job,
    dataset: getDataset(state, job.datasetId),
    isContinuous: getIsContinuous(state, id),
    transformedModels: getTransformedSortedModels(state, id)
  };
}

export function getJobsRow(state) {
  const jobs = getJobsByArr(state);

  return jobs.map((job) => [
    moment.utc(job.createdDate).local().format("MMMM Do YYYY, h:mm:ss a"),
    job.setup && job.setup.label ? job.setup.label + " (" + job.id + ")" : job.id,
    job.dataset ? job.dataset.label : "",
    job.runType,
    job.isContinuous === true ? "Continuous" : "Categorical",
    job.status ? (job.status === "NOT_STARTED" ? "Running" : job.status.toLowerCase()) : undefined,
    job.transformedModels && job.transformedModels.length > 0 ? job.transformedModels[0].label : "...",
    job.transformedModels && job.transformedModels.length > 0
      ? job.transformedModels[0].maxIteration.displayImpact
        ? Math.round(job.transformedModels[0].maxIteration.displayImpact)
        : Math.round(job.transformedModels[0].checkMax)
      : "...",
    job.createdBy
  ]);
}

export function getModels(state, id) {
  return getJob(state, id).models !== undefined ? getJob(state, id).models : {};
}

export function hasModels(state, id) {
  const job = getJob(state, id);

  return (
    job.status === "ERROR" || job.status === "DONE" || (job.models !== undefined && Object.keys(job.models).length > 1)
  );
}

export function getModel(state, id, modelId) {
  return getModels(state, id)[modelId];
}

export function getSortedModelFeedback(state, runId, modelId) {
  const retFeedback = getModelFeedback(state, runId, modelId) || [];
  return retFeedback.sort((a, b) => b.feedback - a.feedback);
}

export function getModelFeedback(state, runId, modelId) {
  const projectId = projectReducer.getProjectIdFromRunId(state, runId);
  const segments = projectReducer.getProjectSegments(state, projectId, false);
  const segment = segments.find((s) => s && s.job && s.job.job_id === runId);

  const segmentRoles = segment && segment.roles;

  if (!segmentRoles || !modelId) {
    return [];
  }

  const retFeedback = {};

  segmentRoles.forEach((role) => {
    const currFeedback = role && role.preferences && role.preferences.feedback;
    if (!currFeedback) {
      return;
    }

    if (currFeedback[modelId]) {
      const currUser = role && role.role_user;
      const currUserId = currUser && currUser.id;
      retFeedback[currUserId] = {
        ...currUser,
        feedback: currFeedback[modelId]
      };
    }
  });

  return Object.values(retFeedback);
}

export function getDeploy(state, id) {
  return getJob(state, id).deploy;
}

export function getCloudURL(state, id) {
  return getJob(state, id).cloudURL;
}

export function getIsScoreUploading(state, id) {
  return getJob(state, id).isScoreUploading;
}

export function getIsPredictLoading(state, id) {
  return getJob(state, id).isPredictLoading;
}

export function getShowDeploy(state, id) {
  return getJob(state, id).showDeploy;
}

export function getCapValue(state, id) {
  return getJob(state, id).capValue;
}

export function getSpecialIndex(state, id) {
  return getJob(state, id).specialIndex;
}

export function getMode(state, id) {
  return getJob(state, id).mode;
}

export function getIsRightOpen(state, id) {
  return getJob(state, id).isRightOpen;
}

export function getPanelMode(state, id) {
  return getJob(state, id).panelMode;
}

export function getIsNextStep(state, id) {
  return getJob(state, id).isNextStep;
}

export function getExpectedModelCount(state, id) {
  return getJob(state, id).expectedModelCount;
}

export function getExpectedExternalModel(state, id) {
  return getJob(state, id).expectedExternalModel;
}

export function getWizardOptions(state, id) {
  return getJob(state, id).wizardOptions || {};
}

export function getWizardOptionsByType(state, id, type) {
  return getWizardOptions(state, id)[type];
}

export function getSavedFrontier(state, id, projectId, breakpoints) {
  return projectReducer.getFrontierByRunId(state, id, projectId, breakpoints);
  //return getJob(state, id).savedFrontier || {};
}

export function getPredictModels(state, id) {
  const predictModels = getJob(state, id).predictModels;
  const selModel = getSelModel(state, id);

  if (selModel === undefined) {
    return [];
  }

  const hasSelected = predictModels && predictModels.filter((m) => selModel && m.label === selModel.id).length > 0;

  return hasSelected
    ? predictModels
    : predictModels
      ? [...predictModels, { label: selModel.label, value: selModel.label }]
      : [{ label: selModel.label, value: selModel.label }];
}

export function getDeployModel(state, id) {
  const modelId = getDeploy(state, id);
}

export function getModelsArr(state, id) {
  const models = getModels(state, id);

  return Object.keys(models).map((key) => (key === "Current State" ? getCurrentState(state, id) : models[key]));
}

export function getIsEmptyModel(state, id) {
  const models = getModelsArr(state, id);

  return models === undefined || models.filter((m) => m.id !== "Current State").length === 0;
}

export function getCapModel(state, id, projectId) {
  return projectId === undefined ? getFirstModel(state, id) : projectReducer.getCapModel(state, projectId, id);
}

export function getFirstModel(state, id) {
  const projectId = projectReducer.getProjectIdFromRunId(state, id);
  return projectReducer.getCapModel(state, projectId, id);
}

export function getSelModel(state, id, transformedModels) {
  const featureModel = getFeatureSelection(state, id, true) || (getFirstModel(state, id) || {}).id;
  const tModels = transformedModels === undefined ? getTransformedSortedModels(state, id) : transformedModels;

  if (tModels && tModels.length > 0) {
    const filteredModels = tModels.filter((t) => t.id === featureModel);

    return filteredModels.length > 0 ? filteredModels[0] : tModels[0];
  }
  return featureModel;
}

export function getIsBusinessReality(state, id) {
  return getJob(state, id).isBusinessReality;
}

export function getNextModel(model, models, currentRatio, isHideCosts, isLeft) {
  let retModel = undefined;

  const modelRatios = models
    .filter((m) => m.id !== model.id)
    .map((m) => ({
      id: m.id,
      ratio: isHideCosts
        ? (m.maxIteration.falsePositive - model.maxIteration.falsePositive) /
            (m.maxIteration.truePositive - model.maxIteration.truePositive) +
          (isLeft ? -0.01 : 0.01)
        : (model.maxIteration.trueNegative -
            model.maxIteration.falsePositive -
            m.maxIteration.trueNegative +
            m.maxIteration.falsePositive) /
            (m.maxIteration.truePositive -
              m.maxIteration.falseNegative -
              model.maxIteration.truePositive +
              model.maxIteration.falseNegative) +
          (isLeft ? -0.01 : 0.01),
      direction: isLeft ? "LEFT" : "RIGHT"
    }));

  if (isLeft === true) {
    let leftDelta = undefined;

    for (const m of modelRatios) {
      if (currentRatio > m.ratio && m.ratio > 0) {
        if (leftDelta === undefined || leftDelta > currentRatio - m.ratio) {
          leftDelta = currentRatio - m.ratio;
          retModel = m;
        }
      }
    }
  } else {
    let rightDelta = undefined;

    for (const m of modelRatios) {
      if (currentRatio < m.ratio && m.ratio < Infinity) {
        if (rightDelta === undefined || rightDelta > m.ratio - currentRatio) {
          rightDelta = m.ratio - currentRatio;
          retModel = m;
        }
      }
    }
  }

  if (retModel !== undefined) {
    const rModels = models.filter((m) => retModel.id === m.id);
    const rModel = rModels && rModels.length > 0 ? rModels[0] : undefined;

    return rModel !== undefined
      ? {
          model: retModel,
          next: getNextModel(
            rModel,
            models.filter((m) => m.id !== rModel.id),
            retModel.ratio,
            isHideCosts,
            isLeft
          )
        }
      : rModel;
  }

  return retModel;
}

export function getTransformedModels(state, id, useRatio = false, ignoredModels) {
  let conversionGain = getConversionGain(state, id);
  let conversionCost = getConversionCost(state, id);
  let conversionMissed = getConversionMissed(state, id);
  let conversionSaved = getConversionSaved(state, id);

  return getTransformedModelsBad(
    state,
    id,
    conversionGain,
    conversionCost,
    conversionMissed,
    conversionSaved,
    useRatio,
    false,
    ignoredModels
  );
}

export function getMiniModels(state, id, ignoredModels) {
  let retModels = {};

  const miniModels = Object.keys(MINI_BLUEPRINTS).forEach((b) => {
    const miniBlueprint = MINI_BLUEPRINTS[b];

    retModels[b] = getTransformedModelsBad(
      state,
      id,
      miniBlueprint.truePositive,
      miniBlueprint.falsePositive,
      -1 * miniBlueprint.truePositive,
      -1 * miniBlueprint.falsePositive,
      false,
      false,
      ignoredModels
    );
  });

  return retModels;
}

export function getRowCount(state, id) {
  const models = getModelsArr(state, id);

  if (models && models.length > 0 && models[0].iterations && models[0].iterations[1]) {
    return models[0].iterations[1].totalCustomers * 4;
  }

  return 1;
}

export function getPctOfRows(state, id) {
  const dataset = getDatasetFromSetup(state, id);

  const models = getModelsArr(state, id);

  if (dataset && models && models.length > 0 && models[0].iterations && models[0].iterations[1]) {
    return (models[0].iterations[1].totalCustomers * 4) / dataset.rowCount;
  }

  return 1;
}

export function getTransformedModelsDefaultCapacity(state, id) {
  const setup = getSetup(state, id);

  let conversionGain = getConversionGain(state, id);
  let conversionCost = getConversionCost(state, id);
  let conversionMissed = getConversionMissed(state, id);
  let conversionSaved = getConversionSaved(state, id);

  let ratio = false;

  if (setup.defaultSetup && setup.defaultSetup.data) {
    ratio =
      setup.defaultSetup.data.processLimit /
      (Number(setup.defaultSetup.data.truePositive) +
        Number(setup.defaultSetup.data.falsePositive) +
        Number(setup.defaultSetup.data.falseNegative) +
        Number(setup.defaultSetup.data.trueNegative));
  }

  const models =
    setup && setup.defaultSetup
      ? getTransformedModelsBad(
          state,
          id,
          conversionGain,
          conversionCost,
          conversionMissed,
          conversionSaved,
          false,
          ratio
        )
      : undefined;

  return models;
}

export function getGlobalModel(state, id) {
  const setup = getSetup(state, id);

  let conversionGain = getConversionGain(state, id);
  let conversionCost = getConversionCost(state, id);
  let conversionMissed = getConversionMissed(state, id);
  let conversionSaved = getConversionSaved(state, id);

  let ratio = false;

  if (setup.defaultSetup && setup.defaultSetup.data) {
    ratio =
      setup.defaultSetup.data.processLimit /
      (Number(setup.defaultSetup.data.truePositive) +
        Number(setup.defaultSetup.data.falsePositive) +
        Number(setup.defaultSetup.data.falseNegative) +
        Number(setup.defaultSetup.data.trueNegative));
  }

  const models =
    setup && setup.defaultSetup
      ? getTransformedModelsBad(
          state,
          id,
          conversionGain,
          conversionCost,
          conversionMissed,
          conversionSaved,
          false,
          ratio
        )
      : undefined;

  return models && models.length > 0 ? models[0] : undefined;
}

export function getCurrentCapacityModels(state, id, projectId, breakpoints = CAPACITY_BREAKPOINTS, ignoredModels) {
  const capValue = Math.round(projectReducer.getCapValue(state, projectId, id));

  if (!isFinite(capValue)) {
    return;
  }
  const currBreakpoints = {
    [capValue + "%"]: capValue
  };
  const capModels = getCapacityModels(state, id, breakpoints, ignoredModels);

  return capModels[capValue + "%"];
}

export function getSortedCapacityModels(state, id) {
  const ignoredModels = setupReducer.getIgnoredModels(state, id);

  const models = getCapacityModels(state, id, undefined, ignoredModels);

  const sortedModels = {};

  Object.keys(models).forEach((b) => {
    const capModels = models[b];

    if (capModels.length > 0) {
      sortedModels[b] = capModels[0];
    }
  });

  return sortedModels;
}

export function saveFrontier(id, breakpoints = CAPACITY_BREAKPOINTS) {
  return (dispatch, getState) => {
    const frontier = projectReducer.getFrontierByRunId(getState(), id);

    dispatch(updateSavedFrontier(id, frontier));
  };
}

// projectDetails is an object
/*
  {
    projectId,
    segmentId,
    isFast
  }
*/
export function getFrontierList(state, id, projectDetails = undefined) {
  const frontier =
    projectDetails == undefined
      ? getSavedFrontier(state, id)
      : projectReducer.getSegmentFrontier(
          state,
          projectDetails.projectId,
          projectDetails.segmentId,
          projectDetails.isFast
        );

  const keys = frontier ? Object.keys(frontier) : [];

  const keyMap = {};

  for (let key in keys) {
    keyMap[key] = Number(key.replace("%", ""));
  }

  keys.sort((a, b) => keyMap[a] - keyMap[b]);

  return keys.map((key) => frontier[key]);
}

export function getFrontier(state, id, projectId = undefined, breakpoints = CAPACITY_BREAKPOINTS) {
  let savedFrontier = { ...getSavedFrontier(state, id, projectId, breakpoints) };

  return savedFrontier;
}

export function getCapacityModelsFast(
  state,
  id,
  breakpoints = CAPACITY_BREAKPOINTS_ALL,
  ignoredModels,
  sendAll = false
) {
  const job = getJob(state, id);

  const modelFactory = job.modelFactory;

  if (modelFactory === undefined) {
    return {};
  }

  const config = getDefaultSettings(state, id, ignoredModels);

  return modelFactory.getCapacityModelsFast(config, breakpoints, sendAll);
}

export function getCapacityModels(state, id, breakpoints = CAPACITY_BREAKPOINTS, ignoredModels) {
  const job = getJob(state, id);

  const modelFactory = job.modelFactory;

  if (modelFactory === undefined) {
    return {};
  }

  const config = getDefaultSettings(state, id, ignoredModels);

  return modelFactory.getCapacityModels(config, breakpoints);
}

function getDefaultSettings(state, id, ignoredModels = {}) {
  return ModelFactoryConfig.create(
    ignoredModels,
    getSetup(state, id),
    getConversionGain(state, id),
    getConversionCost(state, id),
    getConversionMissed(state, id),
    getConversionSaved(state, id),
    getTotal(state, id),
    getTotalPossibleHits(state, id),
    getProcessLimit(state, id),
    getIsQuickRun(state, id),
    getCurrentROI(state, id),
    getSetupMode(state, id),
    getHideCosts(state, id),
    false,
    false,
    getRealityRatio(state, id),
    setupReducer.getIsForceOptimized(state, id),
    getCostSliderRatio(state, id)
  );
}

function getAllSettings(
  state,
  id,
  conversionGain,
  conversionCost,
  conversionMissed,
  conversionSaved,
  useRatio = false,
  processRatio = false,
  ignoredModels = {}
) {
  return ModelFactoryConfig.create(
    ignoredModels,
    getSetup(state, id),
    conversionGain,
    conversionCost,
    conversionMissed,
    conversionSaved,
    getTotal(state, id),
    getTotalPossibleHits(state, id),
    getProcessLimit(state, id),
    getIsQuickRun(state, id),
    getCurrentROI(state, id),
    getSetupMode(state, id),
    getHideCosts(state, id),
    processRatio,
    useRatio,
    getRealityRatio(state, id),
    setupReducer.getIsForceOptimized(state, id),
    getCostSliderRatio(state, id)
  );
}

export function getTransformedModelsBad(
  state,
  id,
  conversionGain,
  conversionCost,
  conversionMissed,
  conversionSaved,
  useRatio = false,
  processRatio = false,
  ignoredModels = {}
) {
  const job = getJob(state, id);

  const modelFactory = job.modelFactory;

  if (modelFactory === undefined) {
    return [];
  }

  const config = getAllSettings(
    state,
    id,
    conversionGain,
    conversionCost,
    conversionMissed,
    conversionSaved,
    useRatio,
    processRatio,
    ignoredModels
  );

  return modelFactory.getModels(config);
}

export function findNextBest(state, id, model, models) {
  const truePositiveImpact = getConversionGain(state, id);
  const falsePositiveImpact = getConversionCost(state, id);
  const trueNegativeImpact = getConversionSaved(state, id);
  const falseNegativeImpact = getConversionMissed(state, id);
  const labels = getLabels(state, id);

  const allModels = models;
  const iteration = model.maxIteration;

  let changeList = {
    0: [],
    1: [],
    2: [],
    3: []
  };

  let minImpact = undefined;
  for (var next of allModels) {
    for (var i = 0; i < 4; i++) {
      let cCount = undefined;
      let nCount = undefined;

      let cBaseROI = undefined;
      let nBaseROI = undefined;

      if (i === 0) {
        cCount = model.maxIteration.truePositive;
        nCount = next.maxIteration.truePositive;

        cBaseROI =
          model.maxIteration.falsePositiveVal +
          model.maxIteration.trueNegativeVal +
          model.maxIteration.falseNegativeVal;

        nBaseROI =
          next.maxIteration.falsePositiveVal + next.maxIteration.trueNegativeVal + next.maxIteration.falseNegativeVal;
      } else if (i === 1) {
        cCount = model.maxIteration.falsePositive;
        nCount = next.maxIteration.falsePositive;

        cBaseROI =
          model.maxIteration.truePositiveVal + model.maxIteration.trueNegativeVal + model.maxIteration.falseNegativeVal;

        nBaseROI =
          next.maxIteration.truePositiveVal + next.maxIteration.trueNegativeVal + next.maxIteration.falseNegativeVal;
      } else if (i === 2) {
        cCount = model.maxIteration.trueNegative;
        nCount = next.maxIteration.trueNegative;

        cBaseROI =
          model.maxIteration.falsePositiveVal +
          model.maxIteration.truePositiveVal +
          model.maxIteration.falseNegativeVal;

        nBaseROI =
          next.maxIteration.falsePositiveVal + next.maxIteration.truePositiveVal + next.maxIteration.falseNegativeVal;
      } else if (i === 3) {
        cCount = model.maxIteration.falseNegative;
        nCount = next.maxIteration.falseNegative;

        cBaseROI =
          model.maxIteration.falsePositiveVal + model.maxIteration.truePositiveVal + model.maxIteration.trueNegativeVal;

        nBaseROI =
          next.maxIteration.falsePositiveVal + next.maxIteration.truePositiveVal + next.maxIteration.trueNegativeVal;
      }

      const nextImpact = (nBaseROI - cBaseROI) / (cCount - nCount);
      if (i === 0) {
        if (
          (model.maxIteration.truePositiveVal > 0 && nextImpact > 0) ||
          (model.maxIteration.truePositiveVal < 0 && nextImpact < 0)
        ) {
          changeList[i].push({
            model: next,
            impact: nextImpact,
            close: Math.abs(Math.abs(truePositiveImpact) - Math.abs(nextImpact)),
            closeRatio: (truePositiveImpact - nextImpact) / truePositiveImpact,
            type: labels.truePositiveLabel,
            impactType: 0
          });
        }
      }
      if (i === 1) {
        if (
          (model.maxIteration.falsePositiveVal > 0 && nextImpact > 0) ||
          (model.maxIteration.falsePositiveVal < 0 && nextImpact < 0)
        ) {
          changeList[i].push({
            model: next,
            impact: nextImpact,
            close: Math.abs(Math.abs(falsePositiveImpact) - Math.abs(nextImpact)),
            closeRatio: (falsePositiveImpact - nextImpact) / falsePositiveImpact,
            type: labels.falsePositiveLabel,
            impactType: 1
          });
        }
      }
      if (i === 2) {
        if (
          (model.maxIteration.trueNegativeVal > 0 && nextImpact > 0) ||
          (model.maxIteration.trueNegativeVal < 0 && nextImpact < 0)
        ) {
          changeList[i].push({
            model: next,
            impact: nextImpact,
            close: Math.abs(Math.abs(trueNegativeImpact) - Math.abs(nextImpact)),
            closeRatio: (trueNegativeImpact - nextImpact) / trueNegativeImpact,
            type: labels.trueNegativeLabel,
            impactType: 2
          });
        }
      }
      if (i === 3) {
        if (
          (model.maxIteration.falseNegativeVal > 0 && nextImpact > 0) ||
          (model.maxIteration.falseNegativeVal < 0 && nextImpact < 0)
        ) {
          changeList[i].push({
            model: next,
            impact: nextImpact,
            close: Math.abs(Math.abs(falseNegativeImpact) - Math.abs(nextImpact)),
            closeRatio: (falseNegativeImpact - nextImpact) / falseNegativeImpact,
            type: labels.falseNegativeLabel,
            impactType: 3
          });
        }
      }
    }
  }

  return {
    ...model,
    changeList: Object.keys(changeList).map((type) =>
      changeList[type].reduce((a, b) => (Math.abs(a.impact) < Math.abs(b.impact) ? a : b), [])
    )
  };
}

// Get the `featureInfo` for the model.
export function getModelFeatureInfoWithDispatch(runId, modelId, feature) {
  return (dispatch, getState) => {
    return getModelFeatureInfo(getState(), runId, modelId, feature);
  };
}

export function getModelFeatureInfo(state, runId, modelId, feature) {
  const retModel = (getModels(state, runId) || {})[modelId];
  const retFeatures =
    retModel && retModel.features && retModel.features.percentages ? retModel.features.percentages : {};

  if (feature === undefined) {
    return retFeatures;
  }
  return retFeatures[feature] ? retFeatures[feature] : {};
}

export function getModelHoldoutDataPath(state, jobId, modelName) {
  const job = getJob(state, jobId);

  if (job !== undefined && job.accountId !== undefined) {
    const account = accountReducer.getAccount(state, job.accountId);

    if (account !== undefined && account.configuration !== undefined && account.configuration.bucket !== undefined) {
      const bucket = account.configuration.bucket;

      const env = globalReducer.getEnvironment(state);

      const prefix = account.type == "AWS" ? "s3://" : "";

      return `${prefix}${bucket}/${env}/models/${jobId}/${modelName}/scored_holdout.zip`;
    }
  }

  return undefined;
}

export function getTransformedModelById(state, id, modelId) {
  const models = getTransformedModels(state, id);

  for (let model of models) {
    if (model.id === modelId) {
      return model;
    }
  }

  return undefined;
}

export function getTransformedSortedModels(state, id, useRatio = false, ignoredModels) {
  const projectId = projectReducer.getProjectIdFromRunId(state, id);
  const retModels = getCurrentCapacityModels(state, id, projectId, CAPACITY_BREAKPOINTS, ignoredModels);

  return retModels;
}

function findBestModelHelper(models, data) {
  let bestModel = models[0];
  for (let i = 1; i < models.length; i++) {
    const currModel = models[i];
    if (data[currModel] > data[bestModel]) {
      bestModel = currModel;
    }
  }
  return bestModel;
}

export function getImpactImprovement(state, id) {
  const data = getCostComparisonPlot(state, id);

  const selectedModel = getFeatureSelection(state, id, true);

  let defaultModel;
  let maxImpactInfo = {
    defImpact: 0,
    bestImpact: 0,
    diff: null
  };
  let selectedImpactComparison = {};
  for (let i = 0; i < data.length; i++) {
    const currData = data[i];
    if (currData.x === "1:1") {
      defaultModel = findBestModelHelper(currData.modelIds, currData);
      break;
    }
  }
  for (let i = 0; i < data.length; i++) {
    const currData = data[i];
    const bestModel = findBestModelHelper(currData.modelIds, currData);
    const bestImpact = currData[bestModel];
    const defImpact = currData[defaultModel];
    const selectedImpact = currData[selectedModel];

    selectedImpactComparison[currData.x] = {
      isBest: bestModel === selectedModel,
      bestModel: bestModel,
      selectedModel: selectedModel,
      selectedImpact,
      bestImpact,
      diff: (bestImpact - selectedImpact) / Math.abs(selectedImpact || 1)
    };

    const currImpactDiff = (bestImpact - defImpact) / Math.abs(defImpact || 1);
    if (maxImpactInfo.diff === null || currImpactDiff > maxImpactInfo.diff) {
      maxImpactInfo = {
        defImpact,
        bestImpact,
        diff: currImpactDiff
      };
    }
  }

  return {
    maxImpactImprovement: maxImpactInfo.diff,
    selectedImpactComparison: selectedImpactComparison
  };
}

export function getRunPlotCapacity(state, id) {
  return getJob(state, id).runPlotCapacity || [0, 100];
}

export function getIsInRunPlotCapacity(state, id, value) {
  const plotConstraints = getRunPlotCapacity(state, id);
  return value >= plotConstraints[0] / 100 && value <= plotConstraints[1] / 100;
}

export function getCostComparisonPlot(state, id, ignoredModels = {}) {
  const plotModels = getMiniModels(state, id, ignoredModels);
  const bestModels = {};
  for (const modelPlotId in plotModels) {
    const modelList = plotModels[modelPlotId];
    const isContinuous = modelList.length > 0 && modelList[0].metrics.sum_over_predict;
    modelList.sort((a, b) => {
      return isContinuous
        ? b.metrics.overImpact + b.metrics.underImpact - (a.metrics.overImpact + a.metrics.underImpact)
        : b.maxIteration.roi - a.maxIteration.roi;
    });

    const allSame = new Set((modelList || []).map((model) => model.maxIteration.roi)).size == 1;

    if (!allSame && modelList && modelList.length > 0) {
      // bestModels[modelList[0].id] = true;
      for (let i = 0; modelList[i].maxIteration.roi === modelList[0].maxIteration.roi; i++) {
        bestModels[modelList[i].id] = true;
      }
      const externalModels = modelList.filter((model) => model.id.includes("external-classifier-"));

      externalModels.forEach((m) => {
        bestModels[m.id] = true;
      });
    }
  }
  const bestModelsList = Object.keys(bestModels);
  return Object.keys(MINI_BLUEPRINTS).map((miniBp) => {
    const currBp = MINI_BLUEPRINTS[miniBp];
    const retData = {
      x: Math.abs(currBp.falsePositive) + ":" + Math.abs(currBp.truePositive),
      xLabel: "Cost Ratio",
      xType: "category",
      yLabel: "Impact",
      models: {},
      modelIds: bestModelsList
    };
    plotModels[miniBp].forEach((currModel) => {
      if (bestModels[currModel.id]) {
        retData[currModel.id] = currModel.metrics.sum_over_predict
          ? currModel.metrics.overImpact + currModel.metrics.underImpact
          : currModel.maxIteration.roi;
        retData.models[currModel.id] = currModel;
      }
    });
    return retData;
  });
}

// tries to reduce the number of ties being shown
// shows the models with the most ties until all the tied points are shown
function resolveTiedModels(tiedModels, tiedPlots) {
  let sortedModelsArr = Object.keys(tiedModels).sort((a, b) => tiedModels[a].length - tiedModels[b].length);
  const retModels = {};
  while (Object.keys(tiedPlots).length) {
    const currModel = sortedModelsArr.pop();
    retModels[currModel] = true;
    for (const plotId of tiedModels[currModel]) {
      delete tiedPlots[plotId];
    }
  }
  return retModels;
}

export function getOptimalContinuousModel(state, id, ignoredModels = {}) {
  const models = getTransformedSortedModels(state, id, undefined, ignoredModels);

  if (models && models[0]) {
    return {
      "0%": models[0]
    };
  } else {
    return undefined;
  }
}

export function getOptimalModelsByCapacityFast(state, id, breakpoints = CAPACITY_BREAKPOINTS_ALL, ignoredModels = {}) {
  const plotModels = getCapacityModelsFast(state, id, breakpoints, ignoredModels, true);

  const keys = Object.keys(plotModels).sort((a, b) => breakpoints[a] - breakpoints[b]);

  const models = {};

  let overallMax = undefined;
  let maxImpact = -Infinity;
  let maxIndex = -1;

  let key, currentMax;

  for (let i = 0; i < keys.length; i++) {
    key = keys[i];
    currentMax = plotModels[key][0];

    if (currentMax) {
      if (maxImpact < currentMax.roi) {
        overallMax = currentMax;
        maxImpact = currentMax.roi;
        maxIndex = i;
      }
    }
  }
  maxIndex = Math.max(0, maxIndex);

  let lastMaxId = undefined;

  if (overallMax) {
    lastMaxId = overallMax.id;
  } else {
    return models;
  }

  let model;

  for (let index = maxIndex; index < keys.length; index++) {
    key = keys[index];
    currentMax = plotModels[key][0];

    if (currentMax) {
      if (lastMaxId !== undefined) {
        for (let j = 1; j < plotModels[key].length; j++) {
          model = plotModels[key][j];

          if (model.roi != currentMax.roi) {
            break;
          } else if (model.id == lastMaxId) {
            currentMax = model;
            break;
          }
        }
      }

      models[key] = currentMax;
      lastMaxId = currentMax.id;
    }
  }

  lastMaxId = overallMax !== undefined ? overallMax.id : undefined;

  for (let i = maxIndex; i-- > 0; ) {
    key = keys[i];
    currentMax = plotModels[key][0];

    if (currentMax) {
      if (lastMaxId !== undefined) {
        for (let j = 1; j < plotModels[key].length; j++) {
          model = plotModels[key][j];

          if (model.roi != currentMax.roi) {
            break;
          } else if (model.id == lastMaxId) {
            currentMax = model;
            break;
          }
        }
      }

      models[key] = currentMax;
      lastMaxId = currentMax.id;
    }
  }

  const final = {};

  keys.forEach((key) => {
    if (models[key] !== undefined) {
      final[key] = models[key];
    }
  });

  return final;
}

export function getOptimalModelsByCapacity(state, id, breakpoints = CAPACITY_BREAKPOINTS, ignoredModels = {}) {
  const plotModels = getCapacityModels(state, id, breakpoints, ignoredModels);

  const keys = Object.keys(plotModels).sort((a, b) => breakpoints[a] - breakpoints[b]);

  const models = {};

  let overallMax = undefined;
  let maxImpact = -Infinity;
  let maxIndex = -1;

  let key, currentMax;

  for (let i = 0; i < keys.length; i++) {
    key = keys[i];
    currentMax = plotModels[key][0];

    if (currentMax) {
      if (maxImpact < currentMax.checkMax) {
        overallMax = currentMax;
        maxImpact = currentMax.checkMax;
        maxIndex = i;
      }
    }
  }
  maxIndex = Math.max(0, maxIndex);

  let lastMaxId = undefined;

  if (overallMax) {
    lastMaxId = overallMax.id;
  } else {
    return models;
  }

  let model;

  for (let index = maxIndex; index < keys.length; index++) {
    key = keys[index];
    currentMax = plotModels[key][0];

    if (currentMax) {
      if (lastMaxId !== undefined) {
        for (let j = 1; j < plotModels[key].length; j++) {
          model = plotModels[key][j];

          if (model.checkMax != currentMax.checkMax) {
            break;
          } else if (model.id == lastMaxId) {
            currentMax = model;
            break;
          }
        }
      }

      models[key] = currentMax;
      lastMaxId = currentMax.id;
    }
  }

  lastMaxId = overallMax !== undefined ? overallMax.id : undefined;

  for (let i = maxIndex; i-- > 0; ) {
    key = keys[i];
    currentMax = plotModels[key][0];

    if (currentMax) {
      if (lastMaxId !== undefined) {
        for (let j = 1; j < plotModels[key].length; j++) {
          model = plotModels[key][j];

          if (model.checkMax != currentMax.checkMax) {
            break;
          } else if (model.id == lastMaxId) {
            currentMax = model;
            break;
          }
        }
      }

      models[key] = currentMax;
      lastMaxId = currentMax.id;
    }
  }

  const final = {};

  keys.forEach((key) => {
    if (models[key] !== undefined) {
      final[key] = models[key];
    }
  });

  return final;
}

export function getCapacityComparisonPlot(state, id, ignoredModels = {}, fids) {
  const runPlotCapacity = getRunPlotCapacity(state, id);
  const fidsModels = fids && fids.size > 0 && Array.from(fids);

  let breakpoints = {};

  const min = runPlotCapacity && runPlotCapacity.length > 0 ? Number(runPlotCapacity[0]) : 0;
  const max = runPlotCapacity && runPlotCapacity.length > 1 ? Number(runPlotCapacity[1]) : 100;

  if (isEnabled("smooth-frontier")) {
    if (min === max || min > max) {
      breakpoints = CAPACITY_BREAKPOINTS_ALL;
    } else {
      for (let i = min; i <= max; i += (max - min) / 10) {
        breakpoints[(!isNaN(i) ? (Number.isInteger(i) ? i : i.toFixed(2)) : i) + "%"] = i / 100;
      }
    }
  } else {
    breakpoints = CAPACITY_BREAKPOINTS_ALL;
  }

  const optimalModelsByCapacity = getCapacityModelsFast(state, id, undefined, ignoredModels);

  const {
    ["0%"]: breakpointZero,
    ["100%"]: breakpointOneHundred,
    ...filteredOptimalModelsByCapacity
  } = optimalModelsByCapacity;

  const plotModels = getCapacityModelsFast(state, id, CAPACITY_BREAKPOINTS_ALL, ignoredModels, true);
  const bestModels = {};
  Object.values(filteredOptimalModelsByCapacity).forEach((m, i) => {
    bestModels[m.id] = true;
  });

  const plotModelsKeys = Object.keys(plotModels);
  if (plotModelsKeys.length > 0) {
    const modelsList = plotModels[plotModelsKeys[0]];
    const externalModels = modelsList.filter((model) => model.id.includes("external-classifier-"));

    if (externalModels.length > 0) {
      externalModels.forEach((m) => {
        bestModels[m.id] = true;
      });
    }
  }

  /*
  const models = getModelsArr(state, id);

  const bestModelsList = [
    ...Object.keys(bestModels),
    ...models
      .sort((a, b) => a.checkMax - b.checkMax)
      .filter((m, i) => i < 10 - Object.keys(bestModels).length)
      .map((m) => m.id),
  ];
  */

  const bestModelsList = Object.keys(bestModels).filter((key) => {
    if (fidsModels && fidsModels.length > 0) {
      return fidsModels.includes(key) || key.includes("external-classifier-");
    }
    return true;
  });

  const breakPointKeys = Object.keys(breakpoints);
  const filteredKeys = breakPointKeys.filter((capBreak) => getIsInRunPlotCapacity(state, id, breakpoints[capBreak]));

  let retPlot = (filteredKeys.length > 0 ? filteredKeys : breakPointKeys).map((capBreak) => {
    const retData = {
      x: String(capBreak).replace("%", ""),
      xLabel: "Population (%) to " + (setupReducer.getTrueAction(state, id) || "Pursue"),
      yLabel: "Impact",
      models: {},
      modelIds: bestModelsList
    };
    plotModels[capBreak].forEach((currModel) => {
      if (bestModels[currModel.id]) {
        retData[currModel.id] =
          currModel.metrics && currModel.metrics.sum_over_predict
            ? currModel.metrics.overImpact + currModel.metrics.underImpact
            : currModel.roi;
        retData.models[currModel.id] = {
          label: currModel.label
        };
      }
    });

    return retData;
  });

  return retPlot;
}

export function getDefaultPlot(state, id, runPlotOverride, ignoredModels = {}) {
  const runPlotType = runPlotOverride || getRunPlotType(state, id);
  const netImpactLabel = getNetImpactLabel(state, id);
  const labels = getLabels(state, id);

  const models = getTransformedModels(state, id, false, ignoredModels);

  const hideCosts = getHideCosts(state, id);
  const isQuickRun = getIsQuickRun(state, id);
  const isTwoMinute = getIsTwoMinuteRun(state, id);
  const falsePositiveImpact = getConversionCost(state, id);
  const falseNegativeImpact = getConversionMissed(state, id);

  const isContinuous = models.length > 0 && !isNaN(models[0].metrics.sum_over_predict);

  return models.map((model) => {
    let x, y, xLabel, yLabel;
    switch (runPlotType) {
      case GRAPH_TYPES.COUNTS:
        if (isContinuous) {
          x = model.metrics.sum_over_predict;
          y = model.metrics.sum_under_predict;
          xLabel = "Sum of Overestimates";
          yLabel = "Sum of Underestimates";
        } else {
          x = Math.round(model.maxIteration.falseNegative);
          y = Math.round(model.maxIteration.falsePositive);
          xLabel = "Underestimate (" + labels.falseNegativeLabel + ")";
          yLabel = "Overestimate (" + labels.falsePositiveLabel + ")";
        }
        break;
      case GRAPH_TYPES.COSTS:
        if (isContinuous) {
          x = model.metrics.overImpact;
          y = model.metrics.underImpact;
          xLabel = "Overestimate Relative Cost";
          yLabel = "Underestimate Relative Cost";
        } else {
          x = Math.round(model.maxIteration.falseNegative) * Math.abs(falseNegativeImpact);
          y = Math.round(model.maxIteration.falsePositive) * Math.abs(falsePositiveImpact);
          xLabel = "Underestimate (" + labels.falseNegativeLabel + ")";
          yLabel = "Overestimate (" + labels.falsePositiveLabel + ")";
        }
        break;
      case GRAPH_TYPES.ACCURACY:
        x = model.maxIteration.accuracy * 100;
        y = model.maxIteration.showConstraint ? model.maxIteration.constraintROI : model.maxIteration.roi;
        xLabel = "Accuracy (%)";
        yLabel = netImpactLabel;
        break;
      case GRAPH_TYPES.PROCESSED:
        x = Math.round(model.maxIteration.falsePositive) + Math.round(model.maxIteration.truePositive);
        y = model.maxIteration.showConstraint ? model.maxIteration.constraintROI : model.maxIteration.roi;
        xLabel = "Processed Count";
        yLabel = netImpactLabel;
        break;
      case GRAPH_TYPES.LOG_LOSS:
        x =
          model.metadata && model.metadata.log_loss !== undefined
            ? model.metadata.log_loss
            : model.maxIteration.accuracy * 100;
        y = model.maxIteration.showConstraint ? model.maxIteration.constraintROI : model.maxIteration.roi;
        xLabel = "Log Loss";
        yLabel = netImpactLabel;
        break;
      case GRAPH_TYPES.ERROR_VS_AVERAGE:
        x = (model.metrics.sum_over_predict - model.metrics.sum_under_predict) / model.metadata.count;
        y = model.metrics.overImpact + model.metrics.underImpact;
        xLabel = "Average Prediction Error";
        yLabel = "Total Error Cost";
        break;
      default:
    }

    return {
      x,
      y,
      model: model,
      xLabel,
      yLabel
    };
  });
}

export function getModelPlotData(state, id, runPlotOverride, fids) {
  const runPlotType = runPlotOverride || getRunPlotType(state, id);

  const ignoredModels = setupReducer.getIgnoredModels(state, id);

  switch (runPlotType) {
    case GRAPH_TYPES.COST_COMPARISON:
      return getCostComparisonPlot(state, id, ignoredModels);
    case GRAPH_TYPES.CAPACITY_COMPARISON:
      return getCapacityComparisonPlot(state, id, ignoredModels, fids);
    default:
      return getDefaultPlot(state, id, runPlotType, ignoredModels);
  }
}

export function getJobID(state, id) {
  return getJob(state, id).jobID;
}

export function getServerProgress(state, id) {
  return getJob(state, id).serverProgress !== undefined ? getJob(state, id).serverProgress : 0;
}

export function getNotebookProgress(state, id) {
  return getJob(state, id).notebookProgress !== undefined ? getJob(state, id).notebookProgress : 0;
}

export function getIterationProgress(state, id) {
  return getJob(state, id).iterationProgress !== undefined ? getJob(state, id).iterationProgress : 0;
}

export function getCompletedProgress(state, id) {
  return getJob(state, id).completedProgress !== undefined ? getJob(state, id).completedProgress : 0;
}

export function getStatus(state, id) {
  return getJob(state, id).status;
}

export function getIsDone(state, id) {
  return getStatus(state, id) === "DONE" || getStatus(state, id) === "ERROR";
}

export function getIsError(state, id) {
  return getStatus(state, id) === "ERROR";
}

export function getFieldSelect(state, id) {
  return getJob(state, id).fieldSelect;
}

export function getValueSelect(state, id) {
  return getJob(state, id).valueSelect;
}

export function getAttributes(state, id) {
  return getJob(state, id).attributes || {};
}

export function getEstimatedSpend(state, id) {
  return getAttributes(state, id).estimated_spend;
}

export function getNotebookCostPerHour(state, id) {
  return getAttributes(state, id).notebook_cost_per_hour;
}

export function getAdjustedThreshold(state, id) {
  return getJob(state, id).adjustedThreshold;
}

export function getErrors(state, id) {
  const annotations = getJob(state, id).annotations;

  return annotations
    ? Object.keys(annotations)
        .filter((aKey) => annotations[aKey].type === "error")
        .map((aKey) => annotations[aKey])
    : undefined;
}

export function getIsContinuous(state, id, forceModels = undefined) {
  const models = forceModels ? forceModels : getModelsArr(state, id);

  if (models.length == 0 && forceModels === undefined) {
    const job = getJob(state, id);

    return job?.isContinuous;
  }

  return models.filter((model) => model.metrics && Object.keys(model.metrics).length > 0).length > 0;
}

function convertLocalDateToUTC(date) {
  var newDate = new Date(date);

  var offset = date.getTimezoneOffset() / 60;
  var hours = date.getHours();

  newDate.setHours(hours + offset);

  return newDate;
}

export function getRunTimes(state, id) {
  const job = getJob(state, id);

  if (job.createdDate) {
    const startTime = new Date(job.createdDate);

    return {
      startTime: startTime,
      endTime: job.endDate ? new Date(job.endDate.replace("+00:00", "")) : undefined
    };
  } else {
    return undefined;
  }
}

export function getCreatedDate(state, id) {
  const job = getJob(state, id);

  return job ? job.createdDate : undefined;
}

export function getEstimatedRunCost(state, id) {
  //return 0.01;
  const runTime = getRunTimes(state, id);
  const job = getJob(state, id);
  const now = convertLocalDateToUTC(new Date());
  const startTime = runTime ? runTime.startTime : undefined;
  const endTime = runTime && runTime.endTime ? runTime.endTime : now;
  const notebook_cost = getNotebookCostPerHour(state, id);
  if (runTime && runTime.startTime && notebook_cost) {
    const runCost = (Math.abs(endTime - startTime) / 1000 / 3600) * notebook_cost * 100;
    return runCost;
  }
  return ((Math.abs(endTime - startTime) / 1000 / 3600) * 5.12 * 100) / 200;
}

export function getRunCost(state, id, projectId) {
  const project = projectReducer.getProject(state, projectId);

  const isOverall = projectReducer.getIsOverallSegment(state, projectId, id);

  if (isOverall === true) {
    let runCost = 0;

    project &&
      project.segments &&
      project.segments.forEach((s) => {
        if (s && s.job && s.job.job_id !== undefined) {
          runCost += getEstimatedSpend(state, s.job.job_id);
        }
      });

    return runCost;
  }

  let estimatedSpend = getEstimatedSpend(state, id);
  return estimatedSpend;
}

export function getRunDatasetFields(state, id) {
  const datasetId = getDatasetId(state, id);
  return getFields(state, datasetId);
}

export function getFeatureSelection(state, id, isReal = false) {
  const stateSelection = getJob(state, id).featureSelection;

  const fModels = getModelsArr(state, id).filter((m) => m.features && Object.keys(m.features).length > 0);

  return stateSelection || isReal === true
    ? stateSelection
    : fModels && fModels.length > 2
      ? fModels[2].id
      : fModels.length > 0
        ? fModels[0].id
        : undefined;
}

export function getSelectedModel(state, id) {
  return getModel(state, id, getFeatureSelection(state, id));
}

export function getChooseModel(state, id) {
  return getJob(state, id).chooseModel;
}

export function getIsDeploy(state, id) {
  return getJob(state, id).isDeploy;
}

export function getPredictMode(state, id) {
  return getJob(state, id).predictMode;
}

export function getDisplayTruePositive(state, id) {
  return getJob(state, id).displayTruePositive;
}

export function getDisplayFalsePositive(state, id) {
  return getJob(state, id).getDisplayFalsePositive;
}

export function getPredictionDriversMode(state, id) {
  return getJob(state, id).predictionDriversMode;
}

export function getTransformedChooseModel(state, id) {
  const chooseModel = getJob(state, id).featureSelection;

  const models = getTransformedSortedModels(state, id).filter((m) => m.id === chooseModel);

  return models && models.length > 0 ? models[0] : undefined;
}

export function getSortedFeatures(state, id, isReal = false) {
  const featureSelection = getFeatureSelection(state, id, isReal);
  const retFeatures =
    featureSelection && getModel(state, id, featureSelection).features
      ? getModel(state, id, featureSelection).features.percentages
      : {};

  return retFeatures
    ? Object.keys(retFeatures)
        .map((fKey) => {
          const feature = retFeatures[fKey];

          const score =
            feature.percentage !== undefined
              ? feature.values[fKey]
                ? feature.values[fKey] * 100
                : feature.percentage * 100
              : feature.weight !== undefined
                ? feature.weight
                : feature;

          return {
            ...feature,
            label: fKey,
            score: score
          };
        })
        .sort((a, b) => Math.abs(b.score) - Math.abs(a.score))
    : [];
}

export function getFeatureBuckets(state, id) {
  const run = getJob(state, id);
  const dataset = getDatasetFromSetup(state, id);

  let ret = {};

  if (run === undefined || run.models === undefined) {
    return ret;
  }

  const model =
    Object.keys(run.models)
      .filter(
        (m) =>
          run.models[m].features !== undefined &&
          run.models[m].features.percentages &&
          Object.keys(run.models[m].features.percentages).length > 0
      )
      .map((m) => run.models[m]).length > 0
      ? Object.keys(run.models)
          .filter(
            (m) =>
              run.models[m].features !== undefined &&
              run.models[m].features.percentages &&
              Object.keys(run.models[m].features.percentages).length > 0
          )
          .map((m) => run.models[m])[0]
      : undefined;

  if (model && model.features && model.features.percentages) {
    Object.keys(model.features.percentages)
      .filter((c) => (dataset && dataset.fields ? dataset.fields[c] : true))
      .forEach((c) => {
        ret[c] = {};

        Object.keys(model.features.percentages[c].values).forEach((v, index) => {
          ret[c][index] = {
            first: v
          };

          if (v && v.indexOf(" to ") !== -1) {
            const { min, value, max } = predictReducer.getFieldRange(v);

            ret[c][index] = {
              min: min,
              max: max,
              value: value
            };
          }
        });
      });
  }

  return ret;
}

export function getFeatureOptions(state, id) {
  const run = getJob(state, id);
  const dataset = getDatasetFromSetup(state, id);

  let ret = {};

  if (run === undefined || run.models === undefined) {
    return ret;
  }

  const model =
    Object.keys(run.models)
      .filter((m) => run.models[m].features !== undefined && Object.keys(run.models[m].features).length > 0)
      .map((m) => run.models[m]).length > 0
      ? Object.keys(run.models)
          .filter((m) => run.models[m].features !== undefined && Object.keys(run.models[m].features).length > 0)
          .map((m) => run.models[m])[0]
      : undefined;

  if (model && model.features && model.features.percentages && dataset && dataset.fields) {
    Object.keys(model.features.percentages)
      .filter((c) => dataset.fields[c] && dataset.fields[c].type === "number")
      .forEach((c) => {
        ret[c] = {
          min: undefined,
          max: undefined
        };

        Object.keys(model.features.percentages[c].values).forEach((v) => {
          if (v && v.indexOf(" to ") !== -1) {
            const { min, max } = predictReducer.getFieldRange(v);

            if (ret[c].min === undefined || min <= ret[c].min) {
              ret[c].min = min;
            }

            if (ret[c].max === undefined || max >= ret[c].max) {
              ret[c].max = max;
            }
          }
        });
      });
  }

  return ret;
}

export function getFeatures(model) {
  let retFeatures = model && model.features ? model.features.percentages : {};

  return retFeatures
    ? Object.keys(retFeatures)
        .map((fKey) => {
          const feature = retFeatures[fKey];

          const score =
            feature.percentage !== undefined
              ? feature.values[fKey]
                ? feature.values[fKey] * 100
                : feature.percentage * 100
              : feature.weight !== undefined
                ? feature.weight
                : feature;

          return {
            ...feature,
            label: fKey,
            score: score
          };
        })
        .sort((a, b) => Math.abs(b.score) - Math.abs(a.score))
    : [];
}

export function getFeaturesByValue(state, id, value) {
  const models = getModelsArr(state, id);

  const features = models
    .filter((model) => getFeatures(model).filter((feature) => feature.label === value).length > 0)
    .map((model) => {
      const feature = getFeatures(model).filter((feature) => feature.label === value);

      return {
        label: model.id,
        value: feature[0].score,
        tooltipLabel: "Percentage"
      };
    })
    .sort((a, b) => b.value - a.value);

  return features;
}

export function getDatasetId(state, id) {
  return getJob(state, id) ? getJob(state, id).datasetId : undefined;
}

export function getAUCData(state, id, modelId) {
  const model = getModel(state, id, modelId);
  return model && model.metadata ? model.metadata.fpr_tpr : undefined;
}

export function getRunPlotType(state, id) {
  return getJob(state, id).runPlotType ? getJob(state, id).runPlotType : null;
}

export function getContinuousCountFromModel(model) {
  if (model && model.metadata && model.metadata.mean_actual) {
    return model.metadata.count * 4;
  }

  if (model && model.metrics && model.metrics.count_under_predict !== undefined) {
    return (model.metrics.count_under_predict + model.metrics.count_over_predict) * 4;
  }
}

export function getContinuousValueFromModel(model) {
  if (model && model.metadata && model.metadata.mean_actual) {
    return model.metadata.mean_actual;
  }

  if (model && model.metrics && model.metrics.count_under_predict !== undefined) {
    return model.metrics.mean_absolute_error / (model.metrics.sum_over_predict + model.metrics.sum_under_predict);
  }
}

export function getContinuousCount(state, id) {
  let retCount = undefined;

  const models = getModels(state, id);

  if (models && Object.keys(models).length > 0) {
    return getContinuousCountFromModel(models[Object.keys(models)[0]]);
  }

  return 1;
}

export function getContinuousValue(state, id) {
  const models = getModels(state, id);

  if (models && Object.keys(models).length > 0) {
    return getContinuousValueFromModel(models[Object.keys(models)[0]]);
  }

  return 1;
}

export function getRealityRatio(state, id) {
  let retRatio = 1;

  const isRealityHammer = setupReducer.getIsRealityHammer(state, id);
  const continuousCount = setupReducer.getBaselineCountContinuous(state, id);
  const continuousValue = setupReducer.getBaselineValueContinuous(state, id);

  const expectedCountContinuous = setupReducer.getExpectedCountContinuous(state, id);

  const expectedValueContinuous = setupReducer.getExpectedValueContinuous(state, id);

  if (isRealityHammer) {
    if (expectedCountContinuous !== undefined && continuousCount !== undefined) {
      retRatio *= expectedCountContinuous / continuousCount;
    }

    if (expectedValueContinuous !== undefined && continuousValue !== undefined) {
      retRatio *= expectedValueContinuous / continuousValue;
    }
  }
  return retRatio;
}

export function getRelatedJobs(state, id) {
  const jobs = getSortedJobsByArr(state, id);
  const job = getTransformedJob(state, id);
  const dataset = getDatasetFromSetup(state, id);
  const splitGroups = getSplitColumn(state, id);
  const splitColumn = getSplitColumn(state, id) ? getSplitColumn(state, id).column : undefined;

  if (dataset && splitColumn && dataset.fields && dataset.fields[splitColumn]) {
    const relatedJobs = jobs.filter((j) => j.datasetId === job.datasetId);

    let retJobs = {
      All: job
    };

    if (splitGroups.splitGroups) {
      Object.keys(splitGroups.splitGroups).map((g) => {
        const group = splitGroups.splitGroups[g];

        let title = "";

        for (const val of Object.keys(group.fields).filter((f) => group.fields[f].isIncluded === true)) {
          title += val + " ";
        }

        retJobs[title] = Number(g);

        for (const rel of relatedJobs) {
          const relSetup = rel.setup;

          let matchCount = 0;

          const relIgnoredValue = setupReducer.getIgnoredValue(state, rel.id);

          if (relSetup && relIgnoredValue && relIgnoredValue[splitColumn]) {
            dataset.fields[splitColumn].data.forEach((f) => {
              if (relIgnoredValue[splitColumn][f.value] === undefined && group.fields[f.value].isIncluded === true) {
                matchCount++;
              }
              if (relIgnoredValue[splitColumn][f.value] === true && group.fields[f.value].isIncluded === false) {
                matchCount++;
              }
            });
          }

          if (matchCount === dataset.fields[splitColumn].data.length) {
            retJobs[title] = rel;
          }
        }
      });
    }

    return retJobs;
  } else {
    return undefined;
  }
}

export function getRelatedId(state, id) {
  return getJob(state, id) ? getJob(state, id).relatedId : undefined;
}

/* Actions */
export function resetJobs() {
  return { type: RESET };
}

export function updateJob(id, job) {
  return { type: UPDATE_JOB, id: id, data: job };
}

export function initWithJobID(id) {
  return updateJob(id, emptyJobState);
}

export function updateServerProgress(id, progress) {
  return { type: UPDATE_FIELD, id: id, data: progress, key: "serverProgress" };
}

export function updateNotebookProgress(id, progress) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: progress,
    key: "notebookProgress"
  };
}

export function updateIterationProgress(id, progress) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: progress,
    key: "iterationProgress"
  };
}

export function updateCompletedProgress(id, progress) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: progress,
    key: "completedProgress"
  };
}

export function updateModel(id, modelId, model) {
  return { type: UPDATE_MODEL, id: id, data: model, modelId: modelId };
}

export function updateModels(id, models) {
  return { type: UPDATE_FIELD, id: id, data: models, key: "models" };
}

export function updateMode(id, mode) {
  return { type: UPDATE_FIELD, id: id, data: mode, key: "mode" };
}

export function updatePredictMode(id, predictMode) {
  return { type: UPDATE_FIELD, id: id, data: predictMode, key: "predictMode" };
}

export function updatePredictionDriversMode(id, predictionDriversMode) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: predictionDriversMode,
    key: "predictionDriversMode"
  };
}

export function updateCloudURL(id, cloudURL) {
  return { type: UPDATE_FIELD, id: id, data: cloudURL, key: "cloudURL" };
}

export function updateStatus(id, status) {
  return { type: UPDATE_FIELD, id: id, data: status, key: "status" };
}

export function updateLabel(id, label) {
  return { type: UPDATE_FIELD, id: id, data: label, key: "label" };
}

export function updateSavedFrontier(id, savedFrontier) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: savedFrontier,
    key: "savedFrontier"
  };
}

export function updateDisplayTruePositive(id, displayTruePositive) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: displayTruePositive,
    key: "displayTruePositive"
  };
}

export function updateDisplayFalsePositive(id, displayFalsePositive) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: displayFalsePositive,
    key: "displayFalsePositive"
  };
}

export function updateIsRightOpen(id, blueprintId, isRightOpen) {
  return (dispatch, getState) => {
    if (isRightOpen === true) {
      dispatch(wizardReducer.scrollToStepById(id, blueprintId, wizardReducer.STEP_TYPE.PROCESS_LIMIT.id));
    }

    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: isRightOpen,
      key: "isRightOpen"
    });
  };
}

export function updatePanelMode(id, blueprintId, panelMode) {
  return (dispatch, getState) => {
    if (panelMode === PANEL_MODES.SELECTED_MODEL) {
      dispatch(wizardReducer.scrollToStepById(id, blueprintId, wizardReducer.STEP_TYPE.PROCESS_LIMIT.id));
    }

    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: panelMode,
      key: "panelMode"
    });
  };
}

export function updateIsNextStep(id, isNextStep) {
  return { type: UPDATE_FIELD, id: id, data: isNextStep, key: "isNextStep" };
}

export function updateRelatedId(id, relatedId, skipSetup = false) {
  return (dispatch, getState) => {
    dispatch(updateStep(500));
    const job = getJob(getState(), id);

    skipSetup !== true && dispatch(updateSetup(id, job.setup));

    dispatch(wizardReducer.updateStepIndex(0));

    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: relatedId,
      key: "relatedId"
    });
  };
}

export function updateIsPredictLoading(id, isPredictLoading) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: isPredictLoading,
    key: "isPredictLoading"
  };
}

export function updateFeatureSelection(id, modelId, scrollToModel = true) {
  return (dispatch, getState) => {
    if (document.getElementById("modelList") && document.getElementById(modelId) && scrollToModel) {
      document.getElementById("modelList").scrollTo({
        top: document.getElementById(modelId).offsetTop - 140,
        behavior: "smooth"
      });
    }

    if (getIsNextStep(getState(), id) !== false) {
      dispatch(updateIsNextStep(id, false));
    }

    const featureSelection = getFeatureSelection(getState(), id, true);

    if (featureSelection === modelId) {
      return Promise.resolve();
    }

    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: modelId,
      key: "featureSelection"
    });
  };
}

export function updateFieldSelect(id, fieldSelect) {
  return { type: UPDATE_FIELD, id: id, data: fieldSelect, key: "fieldSelect" };
}

export function updateValueSelect(id, valueSelect) {
  return { type: UPDATE_FIELD, id: id, data: valueSelect, key: "valueSelect" };
}

export function updateRunPlotType(id, runPlotType) {
  return { type: UPDATE_FIELD, id: id, data: runPlotType, key: "runPlotType" };
}

export function updateSpecialIndex(id, specialIndex) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: specialIndex,
    key: "specialIndex"
  };
}

export function updatePredictModels(id, predictModels) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: predictModels,
    key: "predictModels"
  };
}

export function updateIsScoreUploading(id, isScoreUploading) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: isScoreUploading,
    key: "isScoreUploading"
  };
}

export function updateShowDeploy(id, showDeploy) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: showDeploy,
    key: "showDeploy"
  };
}

// I broke my name scheme here should be update, set maybe does stuff
export function setCapValue(id, capValue) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: capValue,
    key: "capValue"
  };
}

export function updateCapValue(id, projectId, capValue) {
  return (dispatch, getState) => {
    if (capValue === undefined) {
      dispatch({
        type: UPDATE_FIELD,
        id: id,
        data: capValue,
        key: "capValue"
      });

      return dispatch(projectReducer.resetBudget(projectId));
    }

    const totalSize = projectReducer.getCountConfusionMatrix(getState(), projectId, id).getTotal();
    const newConfMatrix = projectReducer.getConfusionMatrix(getState(), id, (capValue / 100) * totalSize);
    const segmentId = projectReducer.getSegmentId(getState(), projectId);

    dispatch(projectReducer.updateSegmentConfusionMatrix(projectId, segmentId, newConfMatrix));

    dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: capValue,
      key: "capValue"
    });

    return dispatch(projectReducer.updatePercentProcessed(projectId, capValue));
  };
}

export function updateAdjustedThreshold(id, adjustedThreshold) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: adjustedThreshold,
    key: "adjustedThreshold"
  };
}

export function updateWizardOption(id, type, option, value) {
  return (dispatch, getState) => {
    let retType = type;

    if (type === undefined) {
      retType = wizardReducer.STEP_TYPE.ACADEMIC_REVIEW_DATASET.id;
    }

    const options = getWizardOptions(getState(), id);
    const newOptions = {
      ...options,
      [retType]: {
        ...options[retType],
        [option]: value
      }
    };
    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      key: "wizardOptions",
      data: newOptions
    });
  };
}

export function toggleDeploy(id) {
  return (dispatch, getState) => {
    const isDeploy = getIsDeploy(getState(), id) ? !getIsDeploy(getState(), id) : true;

    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: isDeploy,
      key: "isDeploy"
    });
  };
}

export function updateChooseModel(id, chooseModel, isNoScroll = false) {
  return (dispatch, getState) => {
    dispatch(updateDeploy(id, undefined, isNoScroll));
    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: chooseModel,
      key: "chooseModel"
    });
  };
}

export function updateIsBusinessReality(id, newVal) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: newVal,
    key: "isBusinessReality"
  };
}

export function updateRunPlotCapacity(id, newVal) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: newVal && newVal.length === 2 ? [Number(newVal[0]), Number(newVal[1])] : newVal,
    key: "runPlotCapacity"
  };
}

export function updateDeploy(id, modelId, isNoScroll = false) {
  return (dispatch, getState) => {
    if (isNoScroll !== true && false) {
      setTimeout(() => {
        window.scrollTo({ top: 999999, behavior: "smooth" });
      }, 400);
    }

    dispatch(updateRightKey("deploy"));

    return dispatch({
      type: UPDATE_FIELD,
      id: id,
      data: modelId,
      key: "deploy"
    });
  };
}

export function hackyDemoStartBars(id, isProd) {
  return (dispatch, getState) => {
    if (getIsDone(getState(), id) !== true) {
      setTimeout(() => getJob(getState(), id).id && dispatch(updateServerProgress(id, 10)), 1000);
      setTimeout(() => getJob(getState(), id).id && dispatch(updateServerProgress(id, 20)), 2000);
      setTimeout(() => getJob(getState(), id).id && dispatch(updateServerProgress(id, 50)), 18000);
      setTimeout(() => getJob(getState(), id).id && dispatch(updateServerProgress(id, 80)), 24000);
      setTimeout(() => getJob(getState(), id).id && dispatch(updateServerProgress(id, 100)), 36000);

      setTimeout(() => getJob(getState(), id).id && dispatch(updateNotebookProgress(id, 5)), 8000);
      setTimeout(() => getJob(getState(), id).id && dispatch(updateNotebookProgress(id, 30)), 24000);
      setTimeout(() => getJob(getState(), id).id && dispatch(updateNotebookProgress(id, 75)), 36000);
      setTimeout(() => getJob(getState(), id).id && dispatch(updateNotebookProgress(id, 80)), 48000);
      setTimeout(() => getJob(getState(), id).id && dispatch(updateNotebookProgress(id, 100)), 54000);
    }
  };
}

function constructModelIteration(model, impactCurves, index) {
  model.adjustedThreshold = parseInt(index);

  const iteration = {
    accuracy: impactCurves.accuracy[index],
    logLoss: impactCurves.log_loss[index],
    breakEvenProbability: impactCurves.prob[index],
    falseNegativeCount: impactCurves.false_negative[index],
    falsePositiveCount: impactCurves.false_positive[index],
    trueNegativeCount: impactCurves.true_negative[index],
    truePositiveCount: impactCurves.true_positive[index]
  };
  iteration.totalCustomers =
    iteration.falseNegativeCount +
    iteration.falsePositiveCount +
    iteration.trueNegativeCount +
    iteration.truePositiveCount;
  model.iterations[1] = iteration;

  model.metadata.log_loss = impactCurves.log_loss[index];
  model.metadata.accuracy_score = impactCurves.accuracy[index];
  model.metadata.break_even_prob = impactCurves.prob[index];

  return model;
}

export function transformModels(modelData, jobID) {
  return (dispatch, getState) => {
    if (modelData && modelData.runs === undefined && modelData.models) {
      modelData.runs = modelData.models;
      delete modelData.models;
    }

    if (modelData && modelData.runs) {
      let models = {};
      for (let model of modelData.runs) {
        const cmff = ConfusionMatrixFactoryFactory.fromRun(model);

        let metrics = model.metrics || model.impact_metrics || {};

        const hasMetrics = Object.values(metrics).some((value) => value != null && value !== undefined);

        if (!hasMetrics) {
          metrics = undefined;
        }

        const label = modelNameMap[model.model_name] || model.model_display_name || model.model_name;

        let newModel = {
          id: model.model_name,
          label: label,
          metrics: model.impact_metrics && model.impact_metrics.sum_over_predict !== null ? model.impact_metrics : {},
          features: model.feature_importance ? model.feature_importance : {},
          plots: model.plots ? model.plots : {},
          metadata: model.metadata ? model.metadata.metrics : undefined,
          extended: model.extended,
          structure:
            model.metadata && model.metadata.type
              ? {
                  type: model.metadata.type,
                  family: model.metadata.structure && model.metadata.structure.family,
                  components: model.metadata.structure && model.metadata.structure.components
                }
              : undefined,
          model_metadata: {
            aible_model: model.model_name.indexOf("aible") !== -1,
            ...(model.model_metadata || {})
          },
          cmff: cmff,
          iterations:
            model.matrices.length > 0
              ? {
                  1: {
                    falseNegativeCount: model.matrices[0][0],
                    falsePositiveCount: model.matrices[0][1],
                    trueNegativeCount: model.matrices[0][2],
                    truePositiveCount: model.matrices[0][3],
                    totalCustomers: model.matrices[0].reduce((a, b) => a + b, 0)
                  }
                }
              : {}
        };

        if (newModel && newModel.features && newModel.features.percentages) {
          for (const fKey of Object.keys(newModel.features.percentages)) {
            let field = newModel.features.percentages[fKey];

            if (field && field.index) {
              const values = [...field.values];
              let retValue = {};

              for (let i = 0; i < values.length; i++) {
                if (field.index && field.index.length > i) {
                  retValue[field.index[i]] = values[i];
                }
              }

              newModel.features.percentages[fKey].values = retValue;
            }
          }
        }

        models[newModel.id] = newModel;
      }

      return models;
    } else if (modelData && modelData.models) {
      let models = getModels(getState(), jobID);

      for (let model of modelData.models) {
        const sModel = getModel(getState(), jobID, model.name);

        let retModel = {
          ...sModel,
          label: model.name,
          id: model.name,
          iterations: sModel
            ? {
                ...sModel.iterations
              }
            : {},
          metrics: model.metrics ? model.metrics : {},
          features: model.feature_importance ? model.feature_importance : {},
          metadata: model.metadata ? model.metadata.metrics : undefined,
          structure:
            model.metadata && model.metadata.type
              ? {
                  type: model.metadata.type,
                  family: model.metadata.structure && model.metadata.structure.family,
                  components: model.metadata.structure && model.metadata.structure.components
                }
              : undefined
        };

        if (model.matrices.length > 0) {
          for (let matrix of model.matrices) {
            retModel.iterations[Object.keys(retModel.iterations).length + 1] = {
              falseNegativeCount: model.matrices[0][0],
              falsePositiveCount: model.matrices[0][1],
              trueNegativeCount: model.matrices[0][2],
              truePositiveCount: model.matrices[0][3],
              totalCustomers: model.matrices[0].reduce((a, b) => a + b, 0)
            };
          }
        }

        if (retModel && retModel.features && retModel.features.percentages) {
          for (const fKey of Object.keys(retModel.features.percentages)) {
            let field = retModel.features.percentages[fKey];

            if (field && field.index) {
              const values = [...field.values];
              let retValue = {};

              for (let i = 0; i < values.length; i++) {
                if (field.index && field.index.length > i) {
                  retValue[field.index[i]] = values[i];
                }
              }

              retModel.features.percentages[fKey].values = retValue;
            }
          }
        }

        models[model.name] = retModel;
      }

      return models;
    }
  };
}

function getRunType(setup) {
  switch (setup && setup.runType) {
    case undefined:
      return "5 Minute";
    case "TwoMinute":
      return "2 Minute";
    default:
      return "Academic";
  }
}

// currModels are the currently existing models for this job.
export function transformJob(job, shouldUpdateSetup = false, currModels = undefined) {
  return (dispatch, getState) => {
    const state = getState();

    const setupState =
      job && job.attributes && job.attributes.extra ? JSON.parse(job.attributes.extra.setupState) : undefined;

    if (setupState) {
      switch (setupState.blueprintId) {
        case "academic":
        case "aible":
        case "quick":
          delete setupState.blueprintId;
          break;
        default:
      }
    }

    const runType = getRunType(setupState);

    if (shouldUpdateSetup && setupState && job && job.id) {
      dispatch(updateSetup(job.id, setupState));
    }

    const outcomeType = getOutcomeType(state, job.id);

    if (setupState && setupState.datasetId && getDataset(state, setupState.datasetId) === undefined) {
      dispatch(fetchDataset(setupState.datasetId));
    }

    let retJob = {
      ...emptyJobState,
      ...getJob(state, job.id),
      id: job.id,
      attributes: job.attributes,
      name: job.name,
      accountId: job.account_id,
      createdDate: job.created_date,
      endDate: job.end_date,
      createdBy: job.created_by && job.created_by.fullname ? job.created_by.fullname : "Unknown",
      status: job.status,
      outcome: job.outcome_key,
      outcome_value: (job.outcome_value ?? [])[0],
      models: { ...currModels, ...dispatch(transformModels(job, job.id)) },
      datasetId: setupState ? setupState.datasetId : undefined,
      jobSetup: job && job.attributes && job.attributes.extra ? JSON.parse(job.attributes.extra.setupState) : undefined,
      annotations: job.annotations,
      errors: job.annotations.filter((a) => a.type === "error"),
      runType: runType,
      expectedModelCount: 50
    };

    try {
      if (job && job.attributes && job.attributes.models) {
        retJob.expectedModelCount = job.attributes.models.split(",").length;
      }
    } catch (e) {
      console.log(e);
    }

    retJob.modelFactory = ModelFactory.create(retJob);
    retJob.expectedExternalModel = {};

    if (job && job.expected_models) {
      const expectedExternalModel = {};
      expectedExternalModel.expected = job.expected_models.find((model) => model.includes("external-classifier-"));
      let actual = [];
      if (job.runs) {
        actual = job.runs.find((run) => run.model_name.includes("external-classifier-"));
      }
      expectedExternalModel.actual = actual;
      retJob.expectedExternalModel = expectedExternalModel;
    }
    const isContinuous =
      outcomeType === "number" ||
      (retJob.models !== undefined &&
        getIsContinuous(
          undefined,
          undefined,
          Object.keys(retJob.models).map((m) => retJob.models[m])
        ));

    retJob.isContinuous = isContinuous;

    if (setupState && setupState.runType === undefined && isContinuous === false && setupState.mode !== 3) {
      retJob.models["Current State"] = dispatch(buildCurrentState(job.id));
    }

    const currPlotType = getRunPlotType(state, job.id);

    retJob.runPlotType = currPlotType
      ? currPlotType
      : retJob.runType === "Academic"
        ? GRAPH_TYPES.COUNTS
        : GRAPH_TYPES.COUNTS;

    if (isContinuous) {
      if (retJob.runPlotType && retJob.runPlotType !== GRAPH_TYPES.COUNTS && retJob.runPlotType !== GRAPH_TYPES.COSTS) {
        retJob.runPlotType = GRAPH_TYPES.COUNTS;
      }
    }

    return retJob;
  };
}

export function getJobStatus(id) {
  return (dispatch, getState) => {
    return ModelAPI.getRunStatus(id).then((response) => {
      return response;
    });
  };
}

export function pollJobs() {
  return (dispatch, getState) => {
    const MODEL_COUNT_DIFF = 10;
    const POLL_COUNT = 4;

    const activeProjectId = Number(globalReducer.getCurrentProjectId(getState()));

    if (isNaN(activeProjectId)) {
      return;
    }
    const activeProjectRuns = projectReducer.getAllJobsFromOptimizer(getState(), activeProjectId);

    const jobs = activeProjectRuns.filter((j) => !isNaN(j.id) && j.status !== "ERROR" && j.status !== "DONE");

    if (jobs && jobs.length > 0) {
      if (jobs.length > POLL_COUNT) {
        for (let i = jobs.length - 1; i > 0; i--) {
          const k = Math.floor(Math.random() * i);
          const temp = jobs[i];
          jobs[i] = jobs[k];
          jobs[k] = temp;
        }
      }

      const jobsToPoll = [];
      for (let i = 0; i < Math.min(POLL_COUNT, jobs.length); i++) {
        const currJobId = jobs[i].id;
        if (!isNaN(currJobId)) {
          jobsToPoll.push(currJobId);
        }
      }
      return Promise.all(
        jobsToPoll.map((currJobId) => {
          const currJob = getJob(getState(), currJobId);
          return dispatch(getJobStatus(currJobId)).then((newJobStatus) => {
            const newModels = (newJobStatus.models || []).filter(
              (nModel) => currJob?.models?.[nModel?.model_name] === undefined
            );

            const modelCountDiff = newModels.length;
            if (!currJob || isNaN(currJob.id) || newJobStatus.status !== currJob.status || modelCountDiff > 0) {
              return dispatch(
                pollJob(
                  currJobId,
                  false,
                  false,
                  true,
                  false,
                  newJobStatus.status !== currJob.status ? undefined : newModels
                )
              );
            } else {
              return Promise.resolve();
            }
          });
        })
      ).then((newJobs) => {
        if (newJobs && newJobs.some((e) => Boolean(e))) {
          dispatch(projectReducer.resetBudget(activeProjectId));
        }
        return Promise.resolve();
      });
    }

    /*
    for (const job of jobs) {
      dispatch(pollJob(job.id));
    }
    */
  };
}

export function pollForecast(jobId, jobOverride) {
  return (dispatch, getState) => {
    const job = jobOverride ? jobOverride : getJob(getState(), jobId);

    if (!job || !setupReducer.getIsForecast(getState(), job.id)) {
      return Promise.resolve();
    }

    return dispatch(fetchForecast(job.id, undefined, true));
  };
}

export function pollJob(
  jobID,
  shouldUpdateSetup = false,
  checkIfExists = false,
  skipUrl = false,
  balanceBudget = false,
  models = undefined
) {
  return (dispatch, getState) => {
    if (
      jobID !== undefined &&
      jobID !== null &&
      jobID !== "new" &&
      (Object.keys(getJob(getState(), jobID)).length === 0 || checkIfExists === false)
    ) {
      return ModelAPI.getRun(jobID, models)
        .then((response) =>
          dispatch(handleJobResponse(response, shouldUpdateSetup, balanceBudget, models !== undefined))
        )
        .catch((e) => {
          console.log(e);
          return Promise.resolve();
        });
    }

    return Promise.resolve();
  };
}

export function handleJobResponse(job, shouldUpdateSetup = false, balanceBudget = false, checkExistingModels = false) {
  return (dispatch, getState) => {
    const currJob = getJob(getState(), job.id);
    const resModels = job && job.models;
    const currModels = currJob && currJob.models;
    if (
      job.status === "DONE" ||
      job.status === "ERROR" ||
      !currJob ||
      currJob.id !== job.id ||
      checkExistingModels ||
      (resModels && resModels.length) !== (currModels && Object.keys(currModels).length)
    ) {
      job = dispatch(transformJob(job, shouldUpdateSetup, checkExistingModels ? currModels : undefined));

      const setup = getSetup(getState(), job.id);

      const cloudURL = getCloudURL(getState(), job.id);

      if (job.status === "DONE") {
        if (setup && setup.datasetId) {
          dispatch(fetchDataset(setup.datasetId));
        }
        if (!cloudURL) {
          dispatch(fetchCloudURL(job.id));
        }
      }

      dispatch(updateJob(job.id, job));

      if (setupReducer.getIsForecast(getState(), job.id)) {
        return dispatch(pollForecast(job.id)).then(() => Promise.resolve(job));
      }

      const currentModels = getModelsArr(getState(), job.id);
      if (balanceBudget && currentModels && currentModels.length > 0) {
        const projectId = projectReducer.getProjectIdFromRunId(getState(), currJob.id);
        dispatch(projectReducer.resetBudget(projectId));
      }

      return Promise.resolve(job);
    }
    return Promise.resolve(currJob);
  };
}

export function handleEndStep(id, section) {
  return (dispatch, getState) => {
    const isQuickRun = getIsQuickRun(getState(), id);
    const job = getJob(getState(), id);

    if (job && job.status === "DONE" && job.cloudURL === undefined) {
      dispatch(fetchCloudURL(id));
    }

    dispatch(updateRelatedId(id, undefined));

    dispatch(updateStep(500));

    //dispatch(modelGroupReducer.fetchModelGroup(id));

    const interval = setInterval(() => {
      if (document.getElementById("wizardScroll")) {
        document.getElementById("wizardScroll").scrollTo({ top: 999999, behavior: "smooth" });

        clearInterval(interval);
      }
    }, 300);

    return Promise.resolve();
  };
}

export function getCurrentState(state, id) {
  return {
    id: "Current State",
    label: "Current State",
    metrics: {},
    iterations: {
      1: {
        falseNegativeCount: getFalseNegative(state, id),
        falsePositiveCount: getFalsePositive(state, id),
        trueNegativeCount: getTrueNegative(state, id),
        truePositiveCount: getTruePositive(state, id),
        totalCustomers: getTotal(state, id)
      }
    }
  };
}

export function buildCurrentState(jobId) {
  return (dispatch, getState) => {
    const state = getState();

    return {
      id: "Current State",
      label: "Current State",
      metrics: {},
      iterations: {
        1: {
          falseNegativeCount: getFalseNegative(state, jobId),
          falsePositiveCount: getFalsePositive(state, jobId),
          trueNegativeCount: getTrueNegative(state, jobId),
          truePositiveCount: getTruePositive(state, jobId),
          totalCustomers: getTotal(state, jobId)
        }
      }
    };
  };
}

export function getCurrentStateImpact(state, id) {
  const falseNegative = getFalseNegative(state, id);
  const falsePositive = getFalsePositive(state, id);
  const trueNegative = getTrueNegative(state, id);
  const truePositive = getTruePositive(state, id);

  const conversionGain = getConversionGain(state, id);
  const conversionCost = getConversionCost(state, id);

  const conversionSaved = getConversionSaved(state, id);
  const conversionMissed = getConversionMissed(state, id);

  return (
    conversionGain * truePositive +
    conversionCost * falsePositive +
    conversionSaved * trueNegative +
    conversionMissed * falseNegative
  );
}

export function updateCurrentState(jobId) {
  return (dispatch, getState) => {
    const model = dispatch(buildCurrentState(jobId));

    return dispatch(updateModel(jobId, model.id, model));
  };
}

export function parseJob(dispatch, job, initState) {
  const initJobState = initState ? { ...emptyJobState, ...initState } : emptyJobState;

  const runs = job.runs && job.models;

  return {
    ...initJobState,
    id: job.id,
    name: job.name,
    accountId: job.account_id,
    attributes: { ...initJobState.attributes, ...job.attributes },
    models: dispatch(transformModels(job, job.id)),
    createdDate: job.created_date,
    endDate: job.end_date,
    created_by: job.created_by,
    createdBy: job.created_by && job.created_by.fullname ? job.created_by.fullname : "Unknown",
    status: job.status,
    isContinuous: runs && runs.some((run) => run.metrics && Object.keys(run.metrics)).length > 0,
    annotations: job.annotations,
    errors: job.annotations ? job.annotations.filter((a) => a.type === "error") : []
  };
}

export function updateJobs(jobs) {
  return (dispatch, getState) => {
    const currentJobs = getJobs(getState());

    const models = jobs.map((job) =>
      currentJobs[job.id] === undefined ? parseJob(dispatch, job) : parseJob(dispatch, job, currentJobs[job.id])
    );

    const setups = jobs
      .filter((job) => job && job.attributes && job.attributes.extra && job.attributes.extra.setupState)
      .map((job) => ({
        id: job.id,
        data: JSON.parse(job.attributes.extra.setupState)
      }));

    let retModels = {};
    let retSetups = {};

    for (const setup of setups) {
      if (
        false &&
        setup.data &&
        getRunType(setup.data) === "Academic" &&
        !(setup.data.businessSubgroups && setup.data.businessSubgroups[0] && setup.data.businessSubgroups[0].value) &&
        !(setup.data.selectedBlueprintId || setup.data.electedMiniBlueprint)
      ) {
        setup.data.conversionCost = -1;
        setup.data.conversionGain = 1;
        setup.data.conversionMissed = -1;
        setup.data.conversionSaved = 1;
      }
      retSetups[setup.id] = {
        ...setup.data,
        defaultTruePositiveImpact: setup.data.conversionGain,
        defaultFalsePositiveImpact: setup.data.conversionCost,
        defaultTrueNegativeImpact: setup.data.conversionSaved,
        defaultFalseNegativeImpact: setup.data.conversionMissed,
        defaultSetup: { ...setup }
      };
    }

    dispatch(updateSetups(retSetups));

    for (const job of models) {
      const setup = retSetups[job.id];

      const outcomeType = getOutcomeType(getState(), job.id);
      const isContinuous =
        outcomeType === "number" ||
        (job.models !== undefined &&
          getIsContinuous(
            undefined,
            undefined,
            Object.keys(job.models).map((m) => job.models[m])
          ));
      retModels[job.id] = {
        ...job,
        runType:
          setup && setup.runType === undefined
            ? "5 Minute"
            : setup && setup.runType === "TwoMinute"
              ? "2 Minute"
              : "Academic",
        datasetId: setup ? setup.datasetId : undefined,
        isContinuous: isContinuous,
        setup: setup,
        mode: PROJECT_MODES.RUN
      };

      const currPlotType = getRunPlotType(getState(), job.id);
      retModels[job.id].runPlotType = currPlotType
        ? currPlotType
        : isContinuous
          ? GRAPH_TYPES.COUNTS
          : retModels[job.id].runType === "Academic"
            ? GRAPH_TYPES.COUNTS
            : GRAPH_TYPES.ACCURACY;

      if (setup && setup.runType === undefined && isContinuous === false && setup.mode !== 3) {
        retModels[job.id].models["Current State"] = dispatch(buildCurrentState(job.id));
      }
    }

    return dispatch({ type: UPDATE_JOBS, data: retModels });
  };
}

export function fetchNextRuns(fetchAll = false, ignoreLatest = false) {
  return (dispatch, getState) => {
    const state = getState();

    dispatch(updateIsJobsLoading(true));

    const runs = getSortedJobsByArr(state).reverse();

    const latestRunId = paginationReducer.getRunsPage(state);

    let userId = null;
    let isPrivate = loginReducer.getIsSharingPrivate(getState());
    if (isPrivate) {
      userId = loginReducer.getUserID(state);
    }

    const accountId = accountReducer.getSelectedAccountId(getState());

    const options = {
      next: getRunOffset(state),
      latestId: ignoreLatest === true ? undefined : latestRunId,
      fetchAll: fetchAll,
      userId: userId
    };

    return ModelAPI.getRuns(accountId, options).then((rawdata) => {
      dispatch(paginationReducer.storeLatestRun(rawdata));
      const data = rawdata && (getIsMarketplace() ? filterOtherUsers(rawdata, state) : rawdata);
      dispatch(updateJobs(data));

      let datasets = {};

      for (const run of data) {
        if (run && run.attributes && run.attributes.extra && run.attributes.extra.setupState) {
          const setup = JSON.parse(run.attributes.extra.setupState);
          const datasetId = setup.datasetId;
          datasets[datasetId] = true;
        }
      }

      const selectedAccount = accountReducer.getSelectedAccount(getState());

      for (const dKey of Object.keys(datasets)) {
        if (getDataset(state, dKey) === undefined) {
          if (selectedAccount && data && data.length > 0 && selectedAccount.id === data[0].account_id) {
            dispatch(fetchDataset(dKey));
          }
        }
      }

      return dispatch(updateIsJobsLoading(false));
    });
  };
}

export function fetchRun(runId) {
  return (dispatch, getState) => {
    const state = getState();

    if (runId !== null && runId !== undefined && runId !== "new") {
      return ModelAPI.getRun(runId).then((data) => {
        dispatch(updateJobs([data]));

        if (data.status === "DONE") {
          dispatch(fetchCloudURL(data.id));
        }

        // setTimeout(() => dispatch(hackyDemoStartBars(data.id)), 3000);

        let datasets = {};

        for (const run of [data]) {
          if (run && run.attributes && run.attributes.extra && run.attributes.extra.setupState) {
            const setup = JSON.parse(run.attributes.extra.setupState);
            const datasetId = setup.datasetId;
            datasets[datasetId] = true;
          }
        }

        for (const dKey of Object.keys(datasets)) {
          if (getDataset(state, dKey) === undefined) {
            dispatch(fetchDataset(dKey));
          }
        }

        if (setupReducer.getIsForecast(getState(), runId)) {
          return dispatch(pollForecast(runId));
        }

        return Promise.resolve();
      });
    }

    return Promise.resolve();
  };
}

export function fetchCloudURL(id) {
  return (dispatch, getState) => {
    return Promise.resolve();
    /*
    Bring this back if we need link to notebooks anywhere?
    This is spamming backend with requests for each job.

    /*
    return ModelAPI.getCloudURL(id).then((response) =>
      dispatch(
        updateCloudURL(id, response && response.url ? response.url : response)
      )
    );
    */
  };
}

export function removeRun(id) {
  return (dispatch, getState) => {
    let runs = getJobs(getState());

    const retRuns = Object.keys(runs).reduce((acc, currId) => {
      if (currId !== String(id)) {
        acc[currId] = runs[currId];
      }
      return acc;
    }, {});

    return dispatch({ type: REPLACE_JOBS, data: retRuns });
  };
}

export function deleteRun(id) {
  return (dispatch, getState) => {
    const selectedAccountType = accountReducer.getSelectedAccountType(getState()) || "cloud";
    const shouldDelete = confirm(
      "After removing this card Aible will not be able to show the performance of the models developed. " +
        "Aible cannot delete model runs from your " +
        selectedAccountType +
        " account(s), so this action will not delete the actual model files. " +
        "If you wish the remove the model files, you will need to delete them from your " +
        selectedAccountType +
        " account."
    );

    if (shouldDelete) {
      return ModelAPI.deleteRun(id).then((response) => {
        dispatch(removeRun(id));

        return dispatch(push("/"));
      });
    }
  };
}

export function cancelRun(id) {
  return (dispatch, getState) => {
    const selectedAccountType = accountReducer.getSelectedAccountType(getState()) || "cloud";
    const shouldDelete = confirm(
      "After cancelling this model training Aible will not be able to show the performance of the models developed. " +
        "Aible cannot delete model runs from your " +
        selectedAccountType +
        " account(s), so this action will not delete the actual model files. " +
        "If you wish the remove the model files, you will need to delete them from your " +
        selectedAccountType +
        " account."
    );

    if (shouldDelete) {
      return ModelAPI.cancelRun(id).then((response) => {
        dispatch(updateJob(id, dispatch(transformJob(response))));
      });
    }
  };
}

export function initRun(id) {
  return (dispatch, getState) => {
    const state = getState();

    const job = getJob(state, id);

    if (getIsRightOpen(state, id) === true) {
      dispatch(updateIsRightOpen(id, undefined, false));
    }

    if (getPanelMode(state, id) !== PANEL_MODES.CLOSED) {
      dispatch(updatePanelMode(id, undefined, PANEL_MODES.CLOSED));
    }

    if (getMode(state, id) !== PROJECT_MODES.RUN) {
      dispatch(updateMode(id, PROJECT_MODES.RUN));
    }

    if (job && job.status === "DONE") {
      dispatch(wizardReducer.scrollToEnd(10));
      return Promise.resolve();
    } else {
      return dispatch(fetchRun(id)).then(() => {
        return dispatch(wizardReducer.scrollToEnd(10));
      });
    }
  };
}

export function init(jobID, isProd) {
  return (dispatch, getState) => {
    const job = getJob(getState(), jobID);

    if (job && job.status === "DONE") {
      dispatch(fetchCloudURL(jobID));
    }

    if (Object.keys(job).length === 0) {
      dispatch(initWithJobID(jobID));

      dispatch(initSetup(jobID));

      dispatch(pollJob(jobID, true));
      // dispatch(hackyDemoStartBars(jobID, isProd));
    } else {
      if (job && job.status !== "DONE" && job.status !== "ERROR") {
        // dispatch(updateServerProgress(jobID, 100));
        // dispatch(updateNotebookProgress(jobID, 100));
        if (job.isPolling !== true) {
          dispatch(pollJob(jobID, true));
        }
      } else {
        dispatch(handleEndStep(500, "Success"));
      }
    }

    dispatch(setDefaults(jobID));

    dispatch(updateJobId(jobID));

    return Promise.resolve();
  };
}

export default runReducer;
